/*
 * improvemint-pickup.js — Best Buy in-store pickup across PLP, PDP and cart.
 *
 * Contracts this file depends on (see docs/COMMERCE-PDP-PICKER.md, which is the
 * locked design — Model A, PDP is the source of truth):
 *
 *   Endpoint   GET /apps/bestbuy/options?sku=<n>&zip=<n>   → PDP widget JSON
 *              GET /apps/bestbuy/options?skus=a,b,c        → batch eligibility
 *              App-proxy authenticated. NOT /apps/bestbuy/availability — that
 *              route is superseded AND unauthenticated; apps/theme/CLAUDE.md
 *              warns off it by name.
 *
 *   Markup     [data-im-pickup]      PLP card callout, carries data-im-sku
 *              [data-im-fulfillment] PDP picker,      carries data-im-sku
 *              [data-im-variant-index]  hidden, inside the picker on a product
 *                                       that HAS a variant picker: one child per
 *                                       variant with [data-im-variant-id] +
 *                                       [data-im-variant-sku]. This file rewrites
 *                                       data-im-sku from it when the shopper
 *                                       switches variant — see syncPdpSku
 *              [data-im-pickup-state]   text swapped by this file
 *              [data-im-delivery-title] title swapped by this file —
 *                                       `delivery.method`, because Best Buy
 *                                       ships some SKUs and delivers others on
 *                                       an appointment (§3a, 2026-09-24)
 *              [data-im-delivery-state] text swapped by this file
 *              [data-im-delivery-note]  `delivery.note` — IN THE DELIVERY DRAWER,
 *                                       not on the card, since 2026-09-24;
 *                                       hidden when absent
 *              [data-im-zip]            opens the ZIP / store modal
 *              data-state = available | unavailable | unknown
 *              `hidden` on a PDP card   set by this file ONLY from a real answer
 *                                       (no `error`) that says that option is
 *                                       not offered — GP, 2026-10-02: "only show
 *                                       available shipping options". An answer
 *                                       we could not get hides nothing new (the
 *                                       Pickup card Liquid already hid — the
 *                                       lessee restriction, or an explicit
 *                                       `in_store_pickup == false` — stays
 *                                       hidden); see "what the answer lets us
 *                                       say" below. ONE exception (GP,
 *                                       2026-10-28): no store near the ZIP keeps
 *                                       Pickup shown, `unavailable`, radio
 *                                       disabled, marked
 *                                       [data-im-no-stores] — see noStoresZip
 *              [data-im-ship-zip]       the shipping card's "Enter ZIP for
 *                                       dates" link, its whole line while no ZIP
 *                                       is known (2026-10-28); also carries
 *                                       [data-im-zip]. See paintShipZipPrompt
 *              [data-im-sale-blocked]   written on the PDP fieldset while Add to
 *                                       Cart is off: `unsellable` (the answer
 *                                       says so) or `no-option` (a real answer
 *                                       left no card to show). See "nothing can
 *                                       fulfil it: no sale" below. Card
 *                                       `data-state` is NOT that signal: a Best
 *                                       Buy outage paints `unknown`, never a no
 *              [data-im-fulfillment-note]  the live region that says why
 *              [data-im-release]        pre-order / coming soon (GP's D7,
 *                                       2026-10-19), from
 *                                       snippets/improvemint-release-pdp.liquid:
 *                                       the state, `release_date` and the copy.
 *                                       `coming-soon` is a THIRD sale-block kind
 *                                       — see "coming soon: no sale, and a
 *                                       countdown" below
 *              [data-im-none-zip]       the ZIP link the `no-option` line carries
 *                                       (and, since 2026-10-28, the add gate's
 *                                       "no ZIP" line)
 *              [data-im-option-error]   one per card, empty until a press of Add
 *                                       to Cart is refused for THAT option — the
 *                                       add gate's red line (2026-10-28, "ZIP and
 *                                       method before the add" below)
 *              [data-im-gate-*]         on the PDP fieldset: the add gate's words
 *              #Inventory-<section>     Hyper's stock line, overridden only
 *                                       while the sale is blocked
 *
 *   Events     im:home-delivery-card    heard on `document`, from
 *                                       improvemint-delivery.js whenever it shows
 *                                       or hides the THIRD card (with
 *                                       `data-im-home-delivery-shown` on the
 *                                       fieldset). It can withhold a card the
 *                                       answer offers, so "is any option left" is
 *                                       re-settled on it.
 *
 *              im:home-delivery-offer   dispatched on `document` from the
 *                                       options answer,
 *                                       `{ offered: bool, earliestDate: string }`.
 *                                       improvemint-delivery.js unhides its card
 *                                       on `offered` and paints "As soon as
 *                                       <date>" from `earliestDate`, which the
 *                                       answer carries since 2026-09-25 whenever
 *                                       it had a ZIP (see earliestDateFrom). No
 *                                       extra Best Buy call: all of it rides on
 *                                       the fetch the PDP already makes.
 *
 *   Cart       cart attributes, NOT line-item properties. One fulfillment per
 *              cart by design, last PDP selection wins, so a cart can never
 *              carry two choices. *This said "mixed carts are impossible by
 *              construction and no cart-blocking is needed". The first half is
 *              still true of the CHOICE; the second stopped being true of the
 *              CONTENTS:* one choice cannot suit a truck-only appliance beside an
 *              item that ships or is picked up, and since 2026-10-17 the checkout
 *              re-check (improvemint-cart-recheck.js) blocks such a cart with a
 *              message rather than letting it reach checkout. What a mixed cart
 *              SHOULD do is open — GP deciding.
 *              Written on a radio `change` AND re-asserted from whatever is
 *              checked at ADD-TO-CART (2026-09-25) — a `change` alone is not the
 *              same thing as "the choice the shopper is looking at", and the gap
 *              placed ground-shipping orders as store pickups. See
 *              "the cart gets what was SELECTED" below.
 *
 * Sibling: assets/improvemint-delivery.js owns the THIRD card, Home Delivery,
 * and the drawer that picks a Best Buy delivery window. The two files share
 * three things and nothing else:
 *
 *   the radio group   both listen for `change` on it. This file ignores the
 *                     value "home-delivery"; that file ignores the other two.
 *                     Exactly one of them writes for any given selection.
 *   the ZIP           localStorage `im_bb_zip`. Whichever drawer's form writes
 *                     it then fires `im:zip-change` on `document`, tagged with
 *                     its own `detail.source`; both listen and each skips the
 *                     event it raised itself.
 *   the attribute set both POST ALL ELEVEN `bb_*` keys on every write, the
 *                     irrelevant ones as "" (which is how /cart/update.js
 *                     deletes an attribute). That is why the five bb_delivery_*
 *                     keys appear, blank, in every write below: a subset write
 *                     is what leaves a cart carrying half a pickup beside half
 *                     a delivery, and the backend FAILS an incomplete
 *                     home-delivery choice (DELIVERY_WINDOW_MISSING) rather
 *                     than falling back to shipping — a delivery-only appliance
 *                     cannot ship, so there is nothing to fall back to.
 *                     PLUS two display-only pickup keys since 2026-10-17,
 *                     `bb_pickup_ready_date` and `bb_store_address` (pickupFacts
 *                     below): filled on a pickup write, "" on every other one.
 *                     Placement never reads them — deriveFulfillmentChoice
 *                     (order-mapping.ts) reads named keys only — and the
 *                     thank-you / order-status pickup block does
 *                     (apps/bestbuy-sync/extensions/pickup-confirmation).
 *
 * Why the PLP does not call the endpoint for every card: batch mode caps at 25
 * SKUs and, on a cold cache, costs one upstream Best Buy Commerce call PER SKU
 * serialised at 2 req/s — a bucket shared with live order placement. A 48-card
 * grid would be ~24s of upstream traffic contending with checkout. So PLP cards
 * render their eligibility from the `bestbuy_sync.in_store_pickup` metafield at
 * page render, and this file only refines the ones whose metafield is ABSENT.
 *
 * Lessee pickup not allowed (G4, 2026-09-15): an element carrying
 * [data-im-pickup-restricted] (Liquid, from `bestbuy_sync.friends_family_pickup`
 * == false) or an endpoint answer with `pickupUnavailableReason:
 * "friends-family-pickup-not-allowed"` is Store Pickup NEVER — even if another
 * answer says pickup is in stock. On a product card that is the "shipping or
 * delivery only" note; on the PDP the card is HIDDEN (2026-10-02, "only show
 * available options"), rendered so by Liquid and kept so by this file. Best Buy
 * lets nobody but OwnLease's Billing Account holder collect it. The backend
 * enforces the same rule per SKU; this is the UX half.
 *
 * No dependencies. Idempotent.
 */
(function () {
  'use strict';
  if (window.__imPickupInit) return;
  window.__imPickupInit = true;

  var ENDPOINT = '/apps/bestbuy/options';
  var ZIP_KEY = 'im_bb_zip';
  var STORE_KEY = 'im_bb_store';
  var TTL_MS = 10 * 60 * 1000; // 10 min, per the spec's sessionStorage guidance
  var BATCH_MAX = 25;          // server slices at 25 silently; chunk to match

  /* ---------------------------------------------------------------- storage */

  /*
   * Every storage access is wrapped. Safari in private mode throws on write,
   * and a shopper with site data blocked would otherwise take the whole picker
   * down with them — the UI has to degrade to "enter a ZIP", not to a broken
   * page.
   */
  function read(store, key) {
    try { return store.getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { store.setItem(key, value); } catch (e) { /* non-fatal */ }
  }

  function getZip() {
    var z = read(localStorage, ZIP_KEY);
    return /^\d{5}$/.test(z || '') ? z : null;
  }
  function setZip(zip) { write(localStorage, ZIP_KEY, zip); }

  function getStore() {
    try { return JSON.parse(read(localStorage, STORE_KEY) || 'null'); } catch (e) { return null; }
  }
  function setStore(store) { write(localStorage, STORE_KEY, JSON.stringify(store)); }

  function cacheGet(key) {
    try {
      var raw = JSON.parse(read(sessionStorage, 'im_bb_' + key) || 'null');
      if (!raw || Date.now() - raw.t > TTL_MS) return null;
      return raw.v;
    } catch (e) { return null; }
  }
  function cacheSet(key, value) {
    write(sessionStorage, 'im_bb_' + key, JSON.stringify({ t: Date.now(), v: value }));
  }

  /* ----------------------------------------------------------------- fetch */

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /*
   * In-flight single-flight, on top of the sessionStorage cache.
   *
   * The cache only helps once a response has LANDED. Entering a ZIP kicks off
   * the PDP refresh and the store list in the same tick, both for the same
   * sku+zip — measured in the harness as two identical requests racing, since
   * neither had written the cache yet when the other started. The Commerce
   * bucket is 2 req/s and shared with order placement, so a free halving is
   * worth eight lines.
   */
  var inFlight = {};

  // Single-SKU widget payload — PDP only.
  function fetchOptions(sku, zip) {
    var key = 'o:' + sku + ':' + (zip || '');
    var hit = cacheGet(key);
    if (hit) return Promise.resolve(hit);
    if (inFlight[key]) return inFlight[key];

    var url = ENDPOINT + '?sku=' + encodeURIComponent(sku) + (zip ? '&zip=' + encodeURIComponent(zip) : '');
    inFlight[key] = getJSON(url)
      .then(function (data) { cacheSet(key, data); return data; })
      .finally(function () { delete inFlight[key]; });
    return inFlight[key];
  }

  /*
   * Batch eligibility. Chunked at BATCH_MAX because the server slices silently
   * past that — an un-chunked 48-SKU request would return 25 answers and drop
   * 23 cards on the floor with no error. Chunks run in SERIES, not parallel:
   * the upstream bucket is 2 req/s and shared with order placement, so firing
   * them together is how you rate-limit checkout.
   */
  function fetchEligibility(skus) {
    var chunks = [];
    for (var i = 0; i < skus.length; i += BATCH_MAX) chunks.push(skus.slice(i, i + BATCH_MAX));

    var out = {};
    return chunks.reduce(function (chain, chunk) {
      return chain.then(function () {
        return getJSON(ENDPOINT + '?skus=' + chunk.join(','))
          .then(function (data) {
            (data.items || []).forEach(function (it) { out[String(it.sku)] = it; });
          })
          .catch(function () { /* degrade safe: leave these unknown */ });
      });
    }, Promise.resolve()).then(function () { return out; });
  }

  /* ------------------------------------------------------------ PLP cards */

  function cardEls() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-im-pickup]'));
  }

  /*
   * Card copy is merchant-editable (theme settings), but the card text is
   * written here rather than in Liquid, so the strings ride on the modal
   * element as data-*. Falls back to English if the modal is absent — a card
   * with no copy is worse than a card with default copy.
   */
  /*
   * ── NO LEAD TIME OF OUR OWN (2026-10-17) ──────────────────────────────────
   *
   * GP's rule, 2026-10-17: every date a shopper sees is Best Buy's date plus OUR
   * configurable lead time, computed SERVER-SIDE — no Shopify estimate and no
   * second, theme-side lead time. Since bestbuy-sync #293 /apps/bestbuy/options
   * already adds the pickup lead time (/app/commerce → "Store pickup lead time",
   * else "Lead time (all methods)") to each store's `availabilityMsg`,
   * `pickup.eta` and the new `readyDate`.
   *
   * Until this date the theme added ITS OWN lead time on top — `leadDays()` /
   * `applyLead()` here, read from the theme setting `pickup_lead_days` through
   * the store drawer's `data-lead-days` — so setting both moved pickup by their
   * sum. Both functions are gone and nothing reads the setting any more. It is
   * still in config/settings_schema.json, marked deprecated, because removing it
   * would orphan the saved value in settings_data.json. Read on the live theme
   * (#189356146801) on 2026-10-17: not set, so the schema default 0 — nothing
   * was ever double-counted in production.
   */

  var DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  var DAY_KEYS = ['sun','mon','tue','wed','thu','fri','sat'];
  var MONS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  /*
   * "Open until 9 pm" — today's closing time only.
   *
   * The API sends the whole week ("Sun: 10am-8pm; Mon: 10am-9pm; ..."), which is
   * far more than a shopper deciding where to drive needs. This picks the entry
   * for the CURRENT day and keeps only the closing half. Best Buy abbreviates
   * Thursday as "Thurs", so matching is on the first three letters.
   */
  function openUntil(hours) {
    if (!hours) return '';
    var want = DAY_KEYS[new Date().getDay()];
    var parts = String(hours).split(';');
    for (var i = 0; i < parts.length; i++) {
      var seg = parts[i].trim();
      var m = seg.match(/^([A-Za-z]+)\s*:\s*(.+)$/);
      if (!m) continue;
      if (m[1].slice(0, 3).toLowerCase() !== want) continue;
      var span = m[2].trim();
      if (/closed/i.test(span)) return label('closed-today', 'Closed today');
      var close = span.split('-').pop().trim();
      // "9pm" -> "9 pm", which is how the spec reads.
      return close
        ? label('open-until', 'Open until {time}').replace(/\{time\}/g, close.replace(/\s*([ap]\.?m\.?)$/i, ' $1'))
        : '';
    }
    return '';
  }

  /*
   * "Order now - pickup on Thu, Nov 5" — the FALLBACK wording, for an answer
   * with no `readyDate` (see pickupWhen below).
   *
   * Best Buy sends prose with a US-format date ("Order Today, Pick up on
   * 09/11/2026"), already moved out by the pickup lead time server-side. This
   * only reformats it; it adds nothing (it ran the date through a theme-side
   * lead time until 2026-10-17 — see "NO LEAD TIME OF OUR OWN" above). A string
   * with no date is passed through rather than guessed at.
   */
  function pickupLine(msg) {
    if (!msg) return '';
    var text = String(msg);
    var m = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!m) return text;
    var d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]), 12, 0, 0);
    return label('order-on', 'Order now - pickup on {date}')
      .replace(/\{date\}/g, DAYS[d.getDay()] + ', ' + MONS[d.getMonth()] + ' ' + d.getDate());
  }

  var ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

  /*
   * A `readyDate` we may show and write, or ''. Exactly YYYY-MM-DD and a real
   * calendar day (2026-02-30 would roll over to March): a wrong pickup date is
   * worse than none, which is also the rule the thank-you page reads it by
   * (pickup-display.ts → formatPickupDate).
   */
  function validReadyDate(raw) {
    var m = ISO_DATE.exec(String(raw || '').trim());
    if (!m) return '';
    var d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
    if (d.getUTCFullYear() !== Number(m[1]) || d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return '';
    return m[0];
  }

  /*
   * "Ready Tue, Oct 20" — the final "available for pickup" date (2026-10-17).
   *
   * `readyDate` is Best Buy's pickup day + our pickup lead time, computed by
   * /apps/bestbuy/options (lead-time.ts → pickupReadyDate), so this prints it and
   * does no arithmetic. Formatted the way improvemint-delivery.js's dayLabel()
   * formats a home-delivery date — "Tue, Oct 20" — and from UTC parts for the
   * same reason: `new Date('2026-10-20')` is midnight UTC, and local getters
   * west of Greenwich read it as the 19th. The cart line
   * (improvemint-cart-pickup-line.liquid) renders the same words from the cart
   * attribute this date is written to, and the word "Ready" is one theme setting
   * (In-store pickup → "Pickup-ready prefix") for both.
   */
  function readyLine(readyDate) {
    var ok = validReadyDate(readyDate);
    if (!ok) return '';
    var m = ISO_DATE.exec(ok);
    var d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
    return label('ready', 'Ready') + ' ' + DAYS[d.getUTCDay()] + ', ' + MONS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  /*
   * The pickup date for one store (or the card): the server's final `readyDate`
   * when the answer carries one, else Best Buy's (already shifted) prose. The
   * prose is the fallback for the two cases readyDate is absent by design — no
   * ZIP ("As early as tomorrow") and a store whose prose names no day ("Order
   * Today") — and for an endpoint older than #293.
   */
  function pickupWhen(readyDate, msg) {
    return readyLine(readyDate) || pickupLine(msg);
  }

  function label(name, fallback) {
    var m = modalEl();
    return (m && m.getAttribute('data-label-' + name)) || fallback;
  }

  var RESTRICTED_REASON = 'friends-family-pickup-not-allowed';

  /* True once either the page (Liquid) or the endpoint said our lessee can't collect it. Sticky. */
  function isRestricted(el, answer) {
    if (el && answer && answer.pickupUnavailableReason === RESTRICTED_REASON) {
      el.setAttribute('data-im-pickup-restricted', '');
    }
    return !!(el && el.hasAttribute('data-im-pickup-restricted'));
  }

  /*
   * Cache the ZIP button on the element the first time we see it.
   *
   * paintCard writes the label with `textContent`, which wipes every child of
   * the slot — including the button, if Liquid rendered it inside. Querying for
   * the button AFTER that write finds nothing, so the ZIP link would vanish on
   * the first repaint and never come back (an unavailable card would lose it
   * permanently, since that branch clears the slot too). Holding the reference
   * on the element survives both.
   */
  /*
   * Once a ZIP is set the button's whole accessible name becomes "55423",
   * which tells a screen-reader user nothing about what pressing it does.
   * The visible text stays bare (it reads as part of the sentence) and the
   * aria-label carries the meaning.
   */
  function labelZipTrigger(trigger, zip) {
    if (!trigger) return;
    trigger.textContent = zip || label('zip-enter', 'Enter ZIP');
    trigger.setAttribute(
      'aria-label',
      zip ? zipChangeAria(zip) : label('zip-aria-pickup', 'Enter a ZIP code to check in-store pickup')
    );
  }

  /*
   * The spoken name of a ZIP link that already shows a ZIP. Every word here and
   * in label() calls comes off the store drawer's data-label-* (Theme settings →
   * In-store pickup, translated on /es); the English fallbacks are only for a
   * page with no drawer. Until 2026-10-28 these were literals, and /es read
   * them in English.
   */
  function zipChangeAria(zip) {
    return label('zip-aria-change', 'Change ZIP code, currently {zip}').replace(/\{zip\}/g, zip);
  }

  function zipTriggerFor(el) {
    if (!el.__imTrigger) {
      var found = el.querySelector('[data-im-zip]');
      if (found) el.__imTrigger = found;
    }
    return el.__imTrigger || null;
  }

  function paintCard(el, state, zip) {
    var trigger = zipTriggerFor(el); // grab it BEFORE the slot is cleared
    var restricted = isRestricted(el);
    if (restricted) state = 'unavailable';
    el.setAttribute('data-state', state);
    var slot = el.querySelector('[data-im-pickup-state]');
    if (!slot) return;

    if (restricted) {
      slot.textContent = label('ship-only-card', 'Shipping or delivery only');
      return;
    }
    if (state === 'unavailable') {
      slot.textContent = label('unavailable', 'Pickup not available');
      return;
    }
    // available + unknown both invite a ZIP; only the wording and weight differ,
    // and the weight is CSS off data-state.
    slot.textContent = (state === 'available'
      ? label('available', 'Pickup in store')
      : label('prompt', 'Check pickup near')) + ' ';

    if (trigger) {
      labelZipTrigger(trigger, zip);
      slot.appendChild(trigger);
    }
  }

  /*
   * Repaint every card from what we already know. Cheap, no network — called on
   * load, after a ZIP change, and after new cards arrive from an AJAX grid.
   */
  function repaintCards() {
    var zip = getZip();
    cardEls().forEach(function (el) {
      // Whatever state the card already carries is kept — the server-rendered
      // metafield value is authoritative for eligibility, and a refine pass
      // will have overwritten data-state by now if it learned better. Repaint
      // exists to swap the displayed ZIP, not to re-decide eligibility.
      paintCard(el, el.getAttribute('data-state') || 'unknown', zip);
    });
  }

  /*
   * Refine ONLY the cards the metafield could not answer. `in_store_pickup` is
   * populated incrementally by content-enrich, so absent is common and must not
   * be read as false — but absent also means we genuinely do not know, and a
   * small batch call is worth it for those.
   */
  function refineUnknownCards() {
    var unknown = cardEls().filter(function (el) {
      return (el.getAttribute('data-state') || 'unknown') === 'unknown' && !el.hasAttribute('data-im-refined');
    });
    if (!unknown.length) return;

    var skus = [];
    unknown.forEach(function (el) {
      el.setAttribute('data-im-refined', '');
      var s = el.getAttribute('data-im-sku');
      if (s && skus.indexOf(s) === -1) skus.push(s);
    });
    if (!skus.length) return;

    fetchEligibility(skus).then(function (map) {
      var zip = getZip();
      unknown.forEach(function (el) {
        var hit = map[el.getAttribute('data-im-sku')];
        if (!hit) return; // still unknown; leave the prompt as-is
        isRestricted(el, hit); // records the endpoint's reason, if any
        paintCard(el, hit.pickup ? 'available' : 'unavailable', zip);
      });
    });
  }

  /* -------------------------------------------------------------- PDP card */

  function pdpEl() { return document.querySelector('[data-im-fulfillment]'); }

  /*
   * The ship/deliver card's TITLE and its optional note.
   *
   * Best Buy's `<available-for-shipping>` and `<available-for-delivery>` are
   * independent, and until 2026-09-24 /apps/bestbuy/options collapsed them into
   * one `delivery` object that this card labelled "Ground Shipping" whatever it
   * was. A 330 lb refrigerator therefore read, on the live PDP:
   *
   *     Ground Shipping
   *     Home delivery — date scheduled with you
   *
   * — the title contradicting the line beneath it (live QA 2026-09-24). The
   * endpoint now names the real method per SKU (§3a of
   * docs/COMMERCE-PDP-PICKER.md), so the title comes from the answer:
   *
   *   ship-to-home   `method` = "Shipping"                ← the Liquid literal,
   *                                                         byte for byte (both
   *                                                         said "Ground
   *                                                         Shipping" until
   *                                                         2026-09-26)
   *   home delivery  `method` = "Delivery"                  ← renamed by GP
   *                                                            2026-09-25
   *
   * *Since 2026-10-02 the second row only ever reaches a HIDDEN card:* a real
   * answer saying Best Buy will not ship takes this card off the page (paintPdp,
   * "only show available options"). The title is still written, so the hidden
   * card never carries a ship-to-home name over a home-delivery answer.
   *
   * `method` and not `headline`: for a shipped SKU the headline is the generic
   * "Delivery", which would replace the more specific words 8,986 shippable
   * products show today, and for a home delivery the headline is "Home delivery"
   * — the exact title of the THIRD card, so two cards would carry one name.
   * `headline` is kept as the fallback, and the server-rendered literal as the
   * last one, so a silent or older endpoint leaves the card exactly as Liquid
   * rendered it.
   *
   * The default is captured on first paint rather than re-read, because by the
   * time a second answer arrives the span holds whatever the first one wrote.
   *
   * *Since 2026-10-28 the words are the theme's, not `method`'s* — see
   * methodTitle below. The endpoint's English printed over the /es title, and
   * the "Liquid literal" above is a setting (In-store pickup → Shipping card
   * title). `method` / `headline` remain the fallback for an answer with no
   * `fulfillment`.
   */
  /*
   * OUR words for the endpoint's method (2026-10-28). `method` is the endpoint's
   * English ("Shipping" / "Delivery"), so on /es it printed English over the
   * Liquid's Spanish title. `fulfillment` says which of the two it is, and the
   * fieldset carries the matching theme settings (In-store pickup → Shipping
   * card title; Home delivery → option title), translated like every other
   * word on the card. An answer with no `fulfillment` (an older endpoint) still
   * gets its own words, as before.
   */
  function methodTitle(card, delivery) {
    if (!delivery) return '';
    var root = card.closest('[data-im-fulfillment]');
    var mine = root && (delivery.fulfillment === 'ship-to-home'
      ? root.getAttribute('data-im-title-shipping')
      : delivery.fulfillment === 'home-delivery' ? root.getAttribute('data-im-title-delivery') : '');
    return mine || delivery.method || delivery.headline || '';
  }

  function paintDeliveryTitle(card, delivery) {
    var title = card.querySelector('[data-im-delivery-title]');
    if (title) {
      if (card.__imTitleDefault == null) {
        card.__imTitleDefault = (title.textContent || '').trim();
      }
      title.textContent = methodTitle(card, delivery) || card.__imTitleDefault;
    }

    /*
     * `note` is sent for a home delivery only — one plain sentence saying how
     * the date gets settled. IT NO LONGER GOES ON THE CARD: GP reviewed the live
     * preview on 2026-09-24 and called the card "way too much text", and the
     * sentence is about scheduling, so it belongs in the scheduling drawer where
     * it is read at the moment it matters. The slot is now
     * `[data-im-delivery-note]` inside #im-delivery-modal
     * (snippets/improvemint-delivery-modal.liquid).
     *
     * Document-wide selector, not drawer-scoped, on purpose: this file owns the
     * options answer and the drawer's own script (improvemint-delivery.js) never
     * sees `note`, so the seam is one element written from the outside. The
     * drawer is global and rendered once near the end of <body>, so there is
     * exactly one of these.
     *
     * Cleared AND re-hidden when absent, so a variant change or a second ZIP
     * cannot leave an appliance's sentence standing in the drawer of a shipped
     * SKU. `hidden` needs the CSS rule in improvemint-brand.css to beat the
     * author `display`.
     */
    var noteEl = document.querySelector('[data-im-delivery-note]');
    if (!noteEl) return;
    var note = (delivery && delivery.note) || '';
    noteEl.textContent = note;
    noteEl.hidden = !note;
  }

  /*
   * The ship/deliver card's ONE detail line.
   *
   * `delivery.eta` for a home delivery LEADS with "Home delivery — " ("Home
   * delivery — date scheduled with you"). The endpoint says why, in
   * availability.ts: both home-delivery ETAs were written to lead with it
   * BECAUSE this card's title was the hard-coded literal "Ground Shipping", so
   * the ETA had to correct the title above it. That stopped being true when the
   * title started coming from `delivery.method` (§3a) — the card now reads
   * "Delivery / Home delivery — date scheduled with you", which
   * says home delivery twice in two lines.
   *
   * So the prefix is dropped HERE, in the one place that knows the title already
   * carries it, and the remainder is sentence-cased. Only for an answer that
   * says `fulfillment: "home-delivery"` — a shipped SKU's ETA is untouched, and
   * an older endpoint that sends no `fulfillment` is untouched too, so this can
   * only ever remove a duplicate we put there ourselves. Nothing is reworded:
   * the words that survive are the endpoint's own.
   *
   * The backend could drop the prefix instead, now that the title is right, and
   * then this becomes a no-op rather than wrong — which is why it matches the
   * prefix rather than assuming it.
   */
  function deliveryDetailText(delivery) {
    var eta = (delivery && delivery.eta) || '';
    if (!delivery || delivery.fulfillment !== 'home-delivery') return eta;
    var trimmed = eta.replace(/^\s*home\s+delivery\s*(?:[—–-]|:)\s*/i, '');
    if (trimmed === eta || !trimmed) return eta;
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  /*
   * NO ZIP, NO DATE: the shipping card's line asks for one (GP, 2026-10-28: "if
   * we dont have a zip, and we need a zip to show dates, then say something like
   * Enter zip for dates").
   *
   * Without a ZIP the options answer's `delivery.eta` is not Best Buy's: it is
   * the endpoint's generic "5–7 business days" plus our shipping lead time, with
   * no Best Buy date behind it (availability.ts → genericDeliveryEta). The
   * backend is being changed to stop sending it, so it may be absent or present;
   * either way it is never shown while there is no ZIP. The line is the link
   * `[data-im-ship-zip]` instead, reading Theme settings → In-store pickup →
   * "Shipping card, before a ZIP is entered" (`data-im-ship-zip-prompt` on the
   * fieldset). It carries `data-im-zip`, so it opens the same ZIP entry as the
   * Pickup card's link and the add gate's "Enter ZIP" link: the store / ZIP
   * drawer, whose submit re-fetches this answer with the ZIP. It asks Best Buy
   * nothing of its own. With a ZIP the line is Best Buy's own `eta`, verbatim,
   * exactly as before.
   *
   * Liquid renders the link inside the line, so that is also the server-side,
   * pre-answer and no-JS state; "FREE†" is our policy, not a date, and follows it
   * as text. Cached on the card before the line is written, the zipTriggerFor
   * idiom, because a textContent write detaches it. Markup older than the link
   * gets the words without a link, which still states no date.
   */
  function shipZipLink(card) {
    if (!card.__imShipZip) {
      var found = card.querySelector('[data-im-ship-zip]');
      if (found) card.__imShipZip = found;
    }
    return card.__imShipZip || null;
  }

  function paintShipZipPrompt(root, dslot, link, parcelFree) {
    var words = root.getAttribute('data-im-ship-zip-prompt') || 'Enter ZIP for dates';
    dslot.textContent = '';
    if (link) {
      if (link.textContent !== words) link.textContent = words;
      link.hidden = false;
      dslot.appendChild(link);
    } else {
      dslot.appendChild(document.createTextNode(words));
    }
    if (parcelFree) dslot.appendChild(document.createTextNode(' · FREE†'));
  }

  /*
   * Is the home-delivery card on offer for this SKU? BOTH flags, strictly true.
   *
   * ONE expression with TWO readers — announceHomeDeliveryOffer below (which
   * reveals the third card) and homeDeliveryShown() (which asks "is any option
   * left?" before improvemint-delivery.js has said whether it showed the card).
   * Shared deliberately: if these two ever disagreed, the page could block the
   * sale while showing a Home Delivery card, or leave it open with nothing on
   * screen to choose.
   *
   * *This said the second reader was "the middle card's radio in paintPdp (which
   * may only be taken away when there is a third card to take it away FOR)".* That
   * guard went on 2026-10-02: the middle card is now hidden whenever a real answer
   * says Best Buy will not ship, and a page left with no option says so and blocks
   * the add instead of keeping a dead card as the fallback.
   */
  function homeDeliveryOffered(delivery) {
    return !!(delivery && delivery.homeDelivery === true && delivery.datePickerEnabled === true);
  }

  /*
   * Tell improvemint-delivery.js whether the THIRD card has anything behind it.
   *
   * It owns that card; this file owns the answer that decides it. Both flags come
   * from the options fetch the PDP already makes on load, so the gate costs no
   * extra Best Buy call — which is the whole reason it can exist at all
   * (/apps/bestbuy/delivery may only be called on a shopper action, F5/F13).
   *
   *   homeDelivery       Best Buy will deliver this SKU on an appointment
   *   datePickerEnabled  CommerceSettings.homeDeliveryEnabled — a window can
   *                      actually be chosen
   *
   * BOTH, strictly true. Either one false means clicking the card can only end
   * in "there is nothing here", which is the dead card this replaces. Absent
   * reads as false: an older endpoint that sends neither key offers nothing,
   * which is the position that shows no card rather than a broken one.
   *
   * A BOTH-WAYS SKU NOW WORKS TOO — and this needed no theme change, which was
   * the point of writing the gate this way. *This paragraph described a backend
   * gap: `datePickerEnabled` "emitted on the home-delivery-ONLY branch of
   * availability.ts and nowhere else", so a SKU Best Buy will both ship and
   * deliver arrived with `homeDelivery: true` and no `datePickerEnabled` and the
   * card stayed hidden for it even with the switch on. Closed the same day by
   * #175 (`availability.ts`: `...(homeDelivery ? { datePickerEnabled:
   * policy.homeDeliveryEnabled === true } : {})` on the shipping branch), so the
   * gate lights such a SKU up on its own.*
   *
   * Absent still reads as false, so nothing above changes: an older endpoint, or
   * any switch-off answer, offers nothing and shows no card.
   *
   * Not dispatched at all when the fetch failed, so a silent endpoint leaves the
   * server-rendered state (hidden) exactly as Liquid wrote it. Since 2026-10-02
   * the same holds for an answer carrying `error` without `unsellable` — the
   * degrade shape, which used to be announced as "not offered" and so could hand
   * an incomplete home-delivery cart back to shipping (a cart WRITE) because
   * Best Buy was down. An answer we could not get decides nothing.
   */
  /*
   * An explicit earliest home-delivery DATE, when this answer carries one.
   *
   * IT DOES, as of 2026-09-25 — `delivery.earliestDate`, `YYYY-MM-DD`. *This said
   * "it does not today", and that the read was here in anticipation.* GP closed
   * the gap by asking for it: *"I thought we chose to show the earliest date
   * available… Why don't we show Delivery with As soon as..?"*, on a PDP whose
   * card read "See the earliest date for 75024".
   *
   * The date was free all along. `/commerce/productservice` — which
   * /apps/bestbuy/options already calls whenever it has a ZIP, for the shipping ETA
   * and the store list — returns `<deliverydate>` elements, already in this exact
   * format. `availabilityWidget` now forwards the soonest one
   * (`earliestDeliveryDate`). **No new Best Buy call**, so F5/F13 are intact and
   * the shopper-initiated /apps/bestbuy/delivery call sites (the header of
   * improvemint-delivery.js lists them) are still the only ones.
   *
   * An answer without it (no ZIP, a shipped-only SKU, an endpoint older than that
   * change) still returns '' here, and improvemint-delivery.js falls back to
   * offering the date rather than stating it — which is the behaviour every PDP
   * had before.
   *
   * A DATE, not a window: `bb_delivery_date` alone is not a placeable choice, so
   * this only ever paints a line. The actual window still comes from
   * /apps/bestbuy/delivery when the shopper selects the card.
   */
  function earliestDateFrom(delivery) {
    if (!delivery) return '';
    var d = delivery.earliestDate || (delivery.earliest && delivery.earliest.date) || '';
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
  }

  /*
   * ALSO WRITTEN TO THE DOM, because the event alone can be dispatched into the
   * void (2026-09-25).
   *
   * Both scripts are `defer`, this one FIRST (layout/theme.liquid, deliberately),
   * so `defer` runs this script to completion before improvemint-delivery.js
   * executes and registers its `im:home-delivery-offer` listener. During a deferred
   * script `document.readyState` is already "interactive", so init() below runs
   * synchronously rather than waiting for DOMContentLoaded — and when fetchOptions
   * HITS ITS CACHE (sessionStorage, 10 min) it returns `Promise.resolve(hit)`,
   * whose `.then(paintPdp)` runs in the microtask checkpoint at the end of this
   * script. So on any warm cache the options answer is painted, and this event
   * fired, before the only listener exists — and improvemint-delivery.js never
   * unhides the third card for the rest of that page view.
   *
   * Measured on the live theme (#158903664753), 2026-09-25: a reload 178 s after
   * the first view made ZERO `/apps/bestbuy/*` requests, and the script order was
   * improvemint-pickup.js then improvemint-delivery.js, both `defer`. Reproduced
   * with these two files unmodified in that order: the card stays `hidden`,
   * `disabled`, `data-state="unknown"` however good the answer is. It is invisible
   * in most testing because a cart that already holds a home-delivery choice makes
   * Liquid render the card visible server-side, needing no event at all, and
   * because a COLD cache is a real round trip that lands in a later task and works.
   *
   * So the answer is also state on the fieldset, where whoever runs second can read
   * it. Same idiom as the `data-im-cart-*` attributes on the same element. The
   * event is kept: it is what repaints on a variant change and a ZIP change, long
   * after both inits are done.
   */
  function announceHomeDeliveryOffer(delivery) {
    var offered = homeDeliveryOffered(delivery);
    var earliestDate = earliestDateFrom(delivery);
    var root = pdpEl();
    if (root) {
      // "1" / "0", never absent, so a reader can tell "not offered" from "no answer
      // yet" — a failed fetch leaves the attribute off and the server-rendered
      // state stands.
      root.setAttribute('data-im-home-delivery-offer', offered ? '1' : '0');
      root.setAttribute('data-im-home-delivery-earliest', earliestDate);
    }
    document.dispatchEvent(new CustomEvent('im:home-delivery-offer', {
      detail: { offered: offered, earliestDate: earliestDate }
    }));
  }

  /* ------------------------------------------- nothing can fulfil it: no sale
   *
   * GP, 2026-09-25, on the PDP for a Geek Squad refurbished MacBook Air
   * (sku 6676804, $1,688.70):
   *
   *   "both store pickup and delivery are grayed out, but add to cart still is
   *    clickable. One of those options have to be selected in order to be able
   *    to continue to cart."
   *
   * Best Buy refuses to sell that SKU through the Commerce Gateway
   * (SKU_ID_NOT_DISPLAYABLE), so a shopper could pay for something we can never
   * fulfil. The picker was already telling the truth — both cards `unavailable`,
   * no radio selected — and the button underneath it did not care.
   *
   * THE RULE, and it is still deliberately narrow. Add to Cart is disabled when,
   * and only when, the options answer for the SKU on screen is one of:
   *
   *   `unsellable`  it carries `unsellable: true`.
   *   `no-option`   it is a REAL answer — no `error` — and it leaves no card to
   *                 show: pickup not offered (or not ours to offer), Best Buy
   *                 will not ship it, and Home Delivery is not shown. Added
   *                 2026-10-02 with GP's rule, "only show available shipping
   *                 options, IF NO shipping options are available then the SKU
   *                 should be out of stock".
   *
   * `unsellable` is the endpoint's own verdict and it is sent on exactly two
   * findings: Best Buy answered and offers no pickup, no shipping and no
   * delivery; or Best Buy refused the SKU itself with a 4xx
   * SKU_ID_NOT_DISPLAYABLE. `no-option` covers what the endpoint cannot see: a
   * delivery-only appliance whose Home Delivery card is not offered (the owner
   * switch off, or improvemint-delivery.js already told `disabled` / `not
   * home-delivery` this session) and no pickup. Such an order fails placement
   * (NO_SHIPPING_OPTION), so it was a sale we could not fulfil — the picker used
   * to keep a dead middle card as "the fallback" and let it through.
   *
   * Anything else — no answer yet, a rejected fetch, an `error` body (a 5xx, a
   * timeout, a rate-limit, a bad credential, an unrecognised error code), JS off
   * — leaves the button exactly as Liquid rendered it, and paints the cards
   * `unknown` with "couldn't check right now" rather than "not available".
   *
   * WHY NOT `data-state="unavailable"` ON EVERY CARD, which is the obvious
   * reading of the bug. Because it is indistinguishable from an outage, and
   * acting on it would take the whole catalogue off sale during one. The route's
   * degrade path (apps.bestbuy.options.tsx) answers HTTP 200 with
   * `{ pickup: null, delivery: null, error: "…" }` for a Best Buy 500, a timeout
   * and a bad credential alike — the SAME body as the real refusal. Until
   * 2026-10-02 paintPdp painted both cards `unavailable` from it, which is how a
   * Best Buy 401 put "Pickup not available" and "Not available for delivery" on
   * sku 6673091 while Add to Cart (correctly) still worked — a false statement:
   * we did not know. That shape is now `unknown`, it hides nothing and it blocks
   * nothing. `no-option` is decided only from an answer with no `error`, so an
   * outage can never reach it. Measured 2026-09-25, and still the reason: of five
   * HTTP 403s in a 350-SKU probe, four were sellable.
   *
   * WHAT ABOUT "one of those options HAVE to be selected"? When something IS
   * selectable, ensureSelection() in improvemint-delivery.js already selects it —
   * Shipping, then Store Pickup, then Home Delivery — and it runs at the end of
   * every paint, off the same im:home-delivery-offer event. So an empty radio
   * group with a live option is already fixed, and `no-option` is NOT "the group
   * is empty": the group is transiently empty mid-paint, and blocking on that
   * would punish the shopper for a state the script is about to correct.
   * `no-option` is computed from the answer and the cards it leaves on the page,
   * by settleAvailability(), after the paint.
   *
   * Both halves are belt AND braces on purpose: the button is disabled so nobody
   * can press it, and the capture-phase submit listener below refuses the add
   * anyway. Hyper's product-info re-renders the add button on a variant change
   * (`updateSourceFromDestination` copies it wholesale), so a disabled button is
   * a state that can be wiped by something other than us; the submit guard
   * cannot be.
   */
  function atcFormsFor(root) {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var out = [];
    for (var i = 0; i < forms.length; i++) {
      if (isThisProductsAddForm(forms[i], root)) out.push(forms[i]);
    }
    return out;
  }

  /*
   * Remember what the button was before we touched it, so unblocking restores it
   * rather than guessing. `disabled` in particular: Hyper disables the same
   * button for a sold-out or nonexistent variant, and blanket-enabling on
   * unblock would put "Add to cart" on a variant that has none.
   */
  function blockAddButton(btn, label) {
    if (!btn.hasAttribute('data-im-blocked')) {
      btn.setAttribute('data-im-blocked', '');
      btn.__imWasDisabled = !!btn.disabled;
      var slot = btn.querySelector('.btn__text');
      btn.__imTextSlot = slot || null;
      btn.__imTextWas = slot ? slot.textContent : null;
      if (slot && label) slot.textContent = label;
    }
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
  }

  function unblockAddButton(btn) {
    if (!btn.hasAttribute('data-im-blocked')) return;
    btn.removeAttribute('data-im-blocked');
    if (btn.__imTextSlot && btn.__imTextWas != null) btn.__imTextSlot.textContent = btn.__imTextWas;
    btn.disabled = !!btn.__imWasDisabled;
    btn.removeAttribute('aria-disabled');
    btn.__imTextSlot = null;
    btn.__imTextWas = null;
  }

  /*
   * Accelerated checkout is a SECOND way to buy, and it skips the cart entirely —
   * so a blocked Add to Cart beside a live "Buy it now" would be a hole, not a
   * fix. It is off on all seven product templates today
   * (`show_dynamic_checkout: false`), which is why this is three lines rather
   * than a feature: it exists so turning that setting on in Customize cannot
   * quietly reopen the hole. Hidden rather than disabled — the element is
   * Shopify's own and its internals are not ours to reach into.
   */
  function paymentButtonsIn(form) {
    return form.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]');
  }

  /*
   * The line above Add to Cart that says why it is off.
   *
   * Written, never inserted: the region is in the DOM from first paint, which is
   * the precondition for an aria-live region to announce at all (the CSS comment
   * says what is and is not guaranteed there). Identical words are left alone,
   * because re-writing textContent re-announces them.
   *
   * The `no-option` line names the ZIP it is about and carries a link to change
   * it (2026-10-02). The link is the same `[data-im-zip]` trigger the Pickup card
   * uses, so it opens the same drawer; Liquid renders it beside the region,
   * hidden, and it is MOVED in after the words — the zipTriggerFor idiom, cached
   * first, because a textContent write detaches every child.
   */
  function noneZipLink(root) {
    if (!root.__imNoneZip) {
      var found = (root.parentNode || document).querySelector('[data-im-none-zip]');
      if (found) root.__imNoneZip = found;
    }
    return root.__imNoneZip || null;
  }

  function writeNote(root, words, link) {
    var note = document.querySelector('[data-im-fulfillment-note]');
    if (!note) return;
    var a = noneZipLink(root);                   // BEFORE the write detaches it
    var key = words + '\u0000' + (link && words ? link.text : '');
    if (note.__imKey === key) return;
    note.__imKey = key;
    note.textContent = words;
    if (!a) return;
    if (link && words) {
      a.textContent = link.text;
      a.setAttribute('aria-label', link.aria);
      a.hidden = false;
      note.appendChild(document.createTextNode(' '));
      note.appendChild(a);
    } else {
      a.hidden = true;
      // Parked beside the region again, so the next write can find it.
      if (!a.isConnected && note.parentNode) note.parentNode.insertBefore(a, note.nextSibling);
    }
  }

  /*
   * Hyper's stock line — "Available in stock", `#Inventory-<section>`, rendered by
   * the `inventory` block in snippets/product-information-blocks.liquid from
   * SHOPIFY inventory, which knows nothing about whether Best Buy can get the item
   * to this shopper. Under a disabled "Unavailable" button it is a flat
   * contradiction, so while the sale is blocked — and only then — it gives way to
   * one line of our own copy (Theme settings → Improvemint Financial — In-store
   * pickup). An unknown answer never touches it.
   *
   * Hyper's nodes are MARKED, never edited, and ours is a sibling. product-info.js
   * replaces that element's innerHTML on a variant change, which takes our marks
   * and our line with it and puts Hyper's fresh line back — so a swap always falls
   * back to the theme's own rendering, never to a stale override. A variant change
   * drops the block anyway (syncPdpSku), and the next answer re-applies it if it
   * still holds.
   *
   * An untracked product renders the element empty; with nothing to contradict,
   * nothing is added.
   */
  function stockBoxFor(root) {
    var scope = (root.closest && root.closest('product-info')) || document;
    return scope.querySelector('[id^="Inventory-"]');
  }

  function paintStockLine(root, words) {
    var box = stockBoxFor(root);
    if (!box) return;
    var ours = box.querySelector('[data-im-stock-note]');
    var kids = box.children;
    var i;

    if (!words) {
      if (ours) box.removeChild(ours);
      for (i = 0; i < kids.length; i++) kids[i].removeAttribute('data-im-stock-hidden');
      return;
    }
    if (!box.querySelector('.product__inventory:not([data-im-stock-note])')) return;

    for (i = 0; i < kids.length; i++) {
      if (kids[i] !== ours) kids[i].setAttribute('data-im-stock-hidden', '');
    }
    if (!ours) {
      // Hyper's own out-of-stock markup, so the grey status colour and the icon
      // come from section-main-product.css with no CSS of ours.
      ours = document.createElement('p');
      ours.className = 'product__inventory product__inventory--out-of-stock font-body-bolder';
      ours.setAttribute('data-im-stock-note', '');
      var icon = document.createElement('span');
      icon.className = 'product__inventory-icon';
      var text = document.createElement('span');
      text.className = 'product__inventory-text';
      ours.appendChild(icon);
      ours.appendChild(text);
      box.insertBefore(ours, box.firstChild);
    }
    var slot = ours.querySelector('.product__inventory-text');
    if (slot && slot.textContent !== words) slot.textContent = words;
  }

  function noOptionWords(root, zip) {
    if (zip) {
      return (root.getAttribute('data-im-none-note') ||
        'This item isn’t available for pickup or delivery to {zip}.').replace(/\{zip\}/g, zip);
    }
    return root.getAttribute('data-im-none-note-no-zip') ||
      'This item isn’t available for pickup or delivery.';
  }

  /* ----------------------------------- coming soon: no sale, and a countdown
   *
   * GP's D7 (2026-10-19): "if shipping does not go out within 7 days then we can
   * show a label 'Coming soon' and add to cart would have a countdown and say to
   * come back in x time to order." apps/bestbuy-sync decides the state daily
   * (app/lib/catalog/release-state.ts) and puts it on the PRODUCT — a tag, and
   * `bestbuy_sync.release_date` — which Liquid hands this file through
   * [data-im-release] (snippets/improvemint-release-pdp.liquid).
   *
   * THE REAL BLOCK IS NOT HERE. A coming-soon SKU's stock is held at 0 by the
   * backend, which stops quick add, the cart and a hand-built /cart/add too.
   * This file does the PDP's part: the add is blocked through the same four
   * guards as `unsellable` / `no-option` (kind `coming-soon`), the button reads
   * "Coming soon", and the line above it counts down — in the BROWSER, from the
   * date, because Shopify caches the page and a Liquid "in 2 days" would still
   * say 2 days tomorrow.
   *
   * The count: days from today in America/Chicago (the backend's clock) to
   * release − ORDER_WINDOW_DAYS (0 since 2026-10-22, below). At 0 it does NOT unblock: the stock is still 0
   * until the backend's daily run (03:00 Chicago) releases it, so it says the
   * item can be ordered now and offers a refresh. The fulfilment cards are not
   * shown and the options endpoint is not asked — there is nothing to fulfil yet.
   *
   * Unlike the other two kinds, `coming-soon` is a fact about the PRODUCT (the
   * backend tags only a product whose every listed SKU is coming soon), so a
   * variant change keeps it, and Hyper's "Sold out" — which product-info.js and
   * the sticky bar write into the button on every variant change — is written
   * back over on `variant:changed`.
   *
   * A pre-order changes one line here: the shipping card says "Ships from
   * <date>" instead of the endpoint's ETA (preorderEta). Its stock line and
   * badge are Liquid.
   */
  // 0 since 2026-10-22 (GP: "turn off pre-orders from buying"): the backend's
  // PREORDERS_BUYABLE = false (apps/bestbuy-sync release-state.ts) holds every
  // pre-release SKU until its RELEASE date, so the countdown counts to that day.
  // It was 7 (D7: orderable from release − 7). Set it back to 7 only together
  // with PREORDERS_BUYABLE = true, or the line names the wrong day.
  var ORDER_WINDOW_DAYS = 0;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function releaseEl(root) {
    var scope = (root && root.closest && root.closest('product-info')) || document;
    return scope.querySelector('[data-im-release]') || document.querySelector('[data-im-release]');
  }

  function releaseState(root) {
    var el = releaseEl(root);
    return el ? el.getAttribute('data-im-release') || '' : '';
  }

  function releaseAttr(el, name, fallback) {
    var v = el && el.getAttribute(name);
    return v || fallback;
  }

  function isIso(s) { return /^\d{4}-\d{2}-\d{2}$/.test(s || ''); }

  /* Today's calendar date in America/Chicago, or null where Intl cannot say. */
  function chicagoToday() {
    try {
      var s = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date());
      return isIso(s) ? s : null;
    } catch (e) {
      return null;
    }
  }

  function isoDays(fromIso, toIso) {
    return Math.round((Date.parse(toIso + 'T00:00:00Z') - Date.parse(fromIso + 'T00:00:00Z')) / 86400000);
  }

  /* "Oct 28" from the YYYY-MM-DD parts — never through local getters. */
  function shortDate(iso) {
    if (!isIso(iso)) return '';
    return MONTHS[parseInt(iso.slice(5, 7), 10) - 1] + ' ' + parseInt(iso.slice(8, 10), 10);
  }

  /* The words above a coming-soon product's Add to Cart, and a refresh offer at 0. */
  function comingSoonCopy(el) {
    var release = releaseAttr(el, 'data-im-release-date', '');
    var orderDate = releaseAttr(el, 'data-im-release-order-date', '');
    var today = chicagoToday();
    if (!isIso(release) || !today) {
      // Cannot count: name the day if Liquid could, else just say what it is.
      if (orderDate) {
        return { words: releaseAttr(el, 'data-im-release-countdown-date', 'Coming soon · Available to order on {date}').replace(/\{date\}/g, orderDate) };
      }
      return { words: releaseAttr(el, 'data-im-release-button', 'Coming soon') };
    }
    var days = isoDays(today, release) - ORDER_WINDOW_DAYS;
    if (days > 1) {
      return {
        words: releaseAttr(el, 'data-im-release-countdown', 'Coming soon · Available to order in {days} days')
          .replace(/\{days\}/g, String(days)).replace(/\{date\}/g, orderDate)
      };
    }
    if (days === 1) {
      return { words: releaseAttr(el, 'data-im-release-countdown-one', 'Coming soon · Available to order tomorrow').replace(/\{date\}/g, orderDate) };
    }
    return {
      words: releaseAttr(el, 'data-im-release-ready', 'Available to order now.'),
      refresh: releaseAttr(el, 'data-im-release-refresh', 'Refresh the page')
    };
  }

  /* Pre-order: "Ships from Oct 28" for the shipping card, or '' to leave the ETA. */
  function preorderEta(root) {
    if (releaseState(root) !== 'preorder') return '';
    var el = releaseEl(root);
    var date = shortDate(releaseAttr(el, 'data-im-release-date', ''));
    if (!date) return '';
    return releaseAttr(el, 'data-im-release-ship-eta', 'Ships from {date}').replace(/\{date\}/g, date);
  }

  /*
   * The countdown line. Same region and the same re-announce rule as writeNote
   * (identical words are left alone); the refresh offer is a button made here,
   * because the region's only parked link is the ZIP trigger.
   */
  function writeReleaseNote(root) {
    var note = document.querySelector('[data-im-fulfillment-note]');
    if (!note) return;
    var copy = comingSoonCopy(releaseEl(root));
    var key = 'release\u0000' + copy.words + '\u0000' + (copy.refresh || '');
    if (note.__imKey === key) return;
    var zipLink = noneZipLink(root);             // BEFORE the write detaches it
    note.__imKey = key;
    note.textContent = copy.words;
    if (zipLink) {
      zipLink.hidden = true;
      if (!zipLink.isConnected && note.parentNode) note.parentNode.insertBefore(zipLink, note.nextSibling);
    }
    if (copy.refresh) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'im-fulfillment__zip';
      b.setAttribute('data-im-release-reload', '');
      b.textContent = copy.refresh;
      note.appendChild(document.createTextNode(' '));
      note.appendChild(b);
    }
  }

  /* Hyper writes "Sold out" into the very span blockAddButton labelled; put ours back. */
  function relabelBlockedButton(btn, label) {
    var slot = btn.querySelector('.btn__text') || btn.querySelector(':scope > span');
    if (slot && label && slot.textContent !== label) slot.textContent = label;
  }

  /* No options fetch, no cards: there is nothing to fulfil yet. */
  function showComingSoon(root) {
    root.hidden = true;
    root.setAttribute('data-im-coming-soon', '');
    applySaleBlock(root, 'coming-soon');
  }

  /*
   * `kind` is null (sell), 'unsellable', 'no-option' or 'coming-soon' — see the
   * rules above. Every blocking kind goes through the same four guards; only the
   * words differ.
   */
  function applySaleBlock(root, kind) {
    if (!root) return;
    var blocked = kind === 'unsellable' || kind === 'no-option' || kind === 'coming-soon';
    var forms = atcFormsFor(root);
    var buttonLabel = kind === 'coming-soon'
      ? releaseAttr(releaseEl(root), 'data-im-release-button', 'Coming soon')
      : root.getAttribute('data-im-unsellable-button');

    for (var i = 0; i < forms.length; i++) {
      var buttons = forms[i].querySelectorAll('[name="add"]');
      for (var j = 0; j < buttons.length; j++) {
        if (blocked) {
          blockAddButton(buttons[j], buttonLabel);
          if (kind === 'coming-soon') relabelBlockedButton(buttons[j], buttonLabel);
        } else unblockAddButton(buttons[j]);
      }
      var pay = paymentButtonsIn(forms[i]);
      for (var k = 0; k < pay.length; k++) {
        if (blocked) {
          if (!pay[k].hasAttribute('data-im-blocked')) pay[k].setAttribute('data-im-blocked', '');
          pay[k].hidden = true;
        } else if (pay[k].hasAttribute('data-im-blocked')) {
          pay[k].removeAttribute('data-im-blocked');
          pay[k].hidden = false;
        }
      }
    }

    if (blocked) root.setAttribute('data-im-sale-blocked', kind);
    else root.removeAttribute('data-im-sale-blocked');
    // A block outranks the add gate (2026-10-28): its red lines go, the note is ours.
    if (blocked) dropGate(root);

    if (kind === 'coming-soon') {
      writeReleaseNote(root);
    } else if (kind === 'no-option') {
      var zip = getZip();
      writeNote(root, noOptionWords(root, zip), {
        text: zip
          ? (root.getAttribute('data-im-none-zip-change') || 'Change ZIP')
          : (root.getAttribute('data-im-none-zip-enter') || 'Enter ZIP'),
        aria: zip ? zipChangeAria(zip) : label('zip-aria-enter', 'Enter a ZIP code')
      });
    } else if (kind === 'unsellable') {
      writeNote(root, root.getAttribute('data-im-unsellable-note') || '', null);
    } else {
      // Nothing blocked: the line is empty, unless a refused press left the add
      // gate's reason there — re-read now, from answers in hand (2026-10-28).
      regate(root);
    }

    // Coming soon keeps Liquid's own stock line ("Coming soon"), so no overlay.
    paintStockLine(root, blocked && kind !== 'coming-soon' ? (root.getAttribute('data-im-none-stock') || 'Not available in your area') : '');

    // The store drawer's shortcut reads the attribute this just wrote.
    syncAtcButton();
  }

  /* ------------------------------------------- what the answer lets us say
   *
   * GP, 2026-10-02, QA'ing sku 6673091 (LG 65" LX7B): the page read "Pickup —
   * Pickup not available" and "Shipping — Not available for delivery", both
   * greyed out, while Add to Cart worked. Best Buy had answered 401 (a
   * credential), and /apps/bestbuy/options degraded to HTTP 200
   * `{ pickup: null, delivery: null, error: "Commerce 401 for
   * /commerce/productavailability" }` — which this file painted as two definite
   * no's. We did not know. And GP's rule for when we DO know:
   *
   *   "if a shipping option is not available we should not even show it, only
   *    show available shipping options, IF NO shipping options are available
   *    then the SKU should be out of stock correct?"
   *
   * So every answer is first sorted into one of three, and nothing below reads
   * the answer without knowing which it is:
   *
   *   'answer'      a real answer: no `error`. Show ONLY what it offers. A card
   *                 it does not offer is HIDDEN, with its radio disabled, so it
   *                 can never be the selection or reach the cart. If that leaves
   *                 nothing, say so and block the add (`no-option`). One card
   *                 is shown without being offered (2026-10-28): Pickup, when no
   *                 store near the shopper's ZIP has the item, saying so with
   *                 its radio disabled, and only beside an option that IS
   *                 offered (noStoresZip below).
   *   'unsellable'  `unsellable: true`, with or without `error`: the endpoint's
   *                 own verdict that nothing can fulfil this SKU. Every card
   *                 hidden, the add blocked exactly as since 2026-09-25.
   *   'unknown'     no answer (the fetch failed) or `error` without
   *                 `unsellable` — the degrade shape. NEVER "not available":
   *                 nothing is hidden on its strength, the cards say "couldn't
   *                 check right now", and Add to Cart keeps working, because a
   *                 Best Buy outage must not take the catalogue off sale (§3b).
   *
   * "No ZIP yet" is not a fourth state. Best Buy's pickup, shipping and delivery
   * flags come from /commerce/productavailability, which takes no ZIP, so a
   * no-ZIP answer is exactly as definitive about WHICH options exist; a ZIP adds
   * the store list, a dated ETA and the earliest delivery date. A pickup SKU
   * answers a `pickup` object without one, so its card stays — and that card is
   * where the ZIP is entered. *Corrected 2026-10-28: this said it answers
   * `pickup: { eta: "As early as tomorrow" }`. Since that day the endpoint sends
   * no pickup `eta` unless Best Buy gave a date (`etaFromBestBuy`), so with no ZIP
   * the card reads "Available for pickup". And with no ZIP the shipping card's
   * line is "Enter ZIP for dates", never the generic eta (paintShipZipPrompt).*
   */
  function verdictOf(data) {
    if (data && data.unsellable === true) return 'unsellable';
    if (data && !data.error) return 'answer';
    return 'unknown';
  }

  /*
   * "No stores near {zip} have this item" goes UNDER THE PICKUP CARD (GP,
   * 2026-10-28: "i think it should be under the card not above it").
   *
   * The one exception to "only show the options a real answer offers". A real
   * answer that says Best Buy looked near this ZIP and named no store —
   * `pickup: null` + `pickupUnavailableReason: "no-stores-near-zip"` — keeps the
   * Pickup card on the page, its radio disabled and unchecked so it can never be
   * the selection, and its one line saying so in red with the card's own ZIP
   * link reading "Change ZIP". Until this date that answer hid the card, and the
   * add gate's state C then had nowhere to put its words but the line above Add
   * to Cart. Every other card follows the existing rules, and when nothing else
   * can be chosen the card goes again and the `no-option` line says it all
   * (settleAvailability).
   *
   * Returns the ZIP to name, or ''. Never for an answer we could not get (an
   * `error`), never for `storesUnknown` (a ZIP was sent and productservice did not
   * come back: not a "no"), never for a lessee restriction (G4), which has its
   * own words.
   */
  var NO_STORES_REASON = 'no-stores-near-zip';

  function noStoresZip(data) {
    if (verdictOf(data) !== 'answer' || data.pickup || data.pickupUnavailableReason !== NO_STORES_REASON) return '';
    if (data.storesUnknown) return '';
    return String(data.zip || getZip() || '');
  }

  /* Best Buy will parcel-ship it. An endpoint older than §3a sends neither
   * `shipping` nor `fulfillment`, and silence reads as shippable, as it always
   * has. A `fulfillment: "home-delivery"` answer is the truck, not a parcel. */
  function shipOffered(delivery) {
    return !!delivery && delivery.shipping !== false && delivery.fulfillment !== 'home-delivery';
  }

  /* On the page and selectable — the only kind of radio that may be checked. */
  function optionLive(radio) {
    if (!radio || radio.disabled) return false;
    var card = radio.closest && radio.closest('[data-im-fulfillment-option]');
    return !(card && card.hidden);
  }

  /*
   * Is the THIRD card on the page — or about to be?
   *
   * improvemint-delivery.js owns it, and it can withhold a card the answer offers
   * (a session that already heard `disabled`, a SKU it was already refused for),
   * so once it has said what it did — `data-im-home-delivery-shown` — that is
   * believed. Before it has said anything (the warm-cache race described at
   * announceHomeDeliveryOffer, where this file paints before that one has run),
   * the answer is all there is, and it errs towards "shown": a block that lands a
   * moment late is better than one that flashes on and then off.
   */
  function homeDeliveryShown(root, data) {
    var card = root.querySelector('[data-im-fulfillment-option="home-delivery"]');
    if (!card) return false;
    if (!card.hidden) return true;
    if (root.getAttribute('data-im-home-delivery-shown') === '0') return false;
    return homeDeliveryOffered(data && data.delivery);
  }

  /*
   * Decide the sale block from the answer and the cards it left on the page.
   * Last thing in every paint, and again whenever improvemint-delivery.js shows
   * or hides its card — the one card this file does not decide. The cards are
   * read ONLY for an 'answer' verdict: an unknown answer hides nothing, so it can
   * never look like "no option".
   */
  function settleAvailability(root) {
    if (!root) return;
    // Coming soon outranks every answer: a product, not a SKU, verdict.
    if (releaseState(root) === 'coming-soon') { applySaleBlock(root, 'coming-soon'); return; }
    var data = root.__imData;
    var verdict = verdictOf(data);
    var kind = null;
    if (verdict === 'unsellable') {
      kind = 'unsellable';
    } else if (verdict === 'answer') {
      var pickup = root.querySelector('[data-im-fulfillment-option="store-pickup"]');
      var ship = root.querySelector('[data-im-fulfillment-option="delivery"]');
      // A Pickup card kept only to say "no stores near {zip}" (2026-10-28) is on
      // the page but is not an option, so it does not count.
      var noStores = !!(pickup && pickup.hasAttribute('data-im-no-stores'));
      var any = (pickup && !pickup.hidden && !noStores) || (ship && !ship.hidden) || homeDeliveryShown(root, data);
      if (!any) kind = 'no-option';
      // And it stays only beside something that can be chosen. With nothing, it
      // goes again, the fieldset collapses and the `no-option` line above Add to
      // Cart is the whole statement, exactly as before that date. Re-decided here
      // because the Home Delivery card can come and go after the paint.
      if (noStores) pickup.hidden = !any;
    }
    applySaleBlock(root, kind);
  }

  function paintPdp(data) {
    var root = pdpEl();
    if (!root) return;
    // First, so a commitChoice below reads THIS answer's shippingOptionKey and
    // not the previous one's (it read the previous one's until 2026-10-02).
    root.__imData = data || null;
    var verdict = verdictOf(data);
    var known = verdict !== 'unknown';
    var zip = getZip();
    var chosen = getStore();

    var pickupCard = root.querySelector('[data-im-fulfillment-option="store-pickup"]');
    var deliveryCard = root.querySelector('[data-im-fulfillment-option="delivery"]');
    var pickupRadio = pickupCard && pickupCard.querySelector('input[type="radio"]');
    var shipRadio = deliveryCard && deliveryCard.querySelector('input[type="radio"]');
    var restricted = !!pickupCard && isRestricted(pickupCard, data);

    /*
     * Store Pickup. A real answer shows it only when Best Buy offers pickup AND
     * our lessee may collect it (G4). An unknown answer hides only what we know
     * without Best Buy — the lessee restriction, and a card Liquid already
     * rendered hidden from an explicit `in_store_pickup == false` — and it never
     * ENABLES the radio, because a pickup needs a store list we did not get.
     * Liquid's verdict is captured once, before any paint moves it.
     */
    if (pickupCard && pickupCard.__imServerHidden == null) pickupCard.__imServerHidden = pickupCard.hidden;
    var pickupSelectable = verdict === 'answer' && !restricted && !!data.pickup;
    // No store near this ZIP (2026-10-28): shown, never selectable — see noStoresZip.
    var noStores = restricted ? '' : noStoresZip(data);
    var pickupVisible = known ? (pickupSelectable || !!noStores) : (!restricted && !(pickupCard && pickupCard.__imServerHidden));

    if (pickupCard) {
      // Same ordering trap as paintCard: cache the button BEFORE the slot is
      // cleared, because Liquid may render it inside [data-im-pickup-state].
      var trigger = zipTriggerFor(pickupCard);
      var slot = pickupCard.querySelector('[data-im-pickup-state]');

      pickupCard.hidden = !pickupVisible;
      pickupCard.setAttribute('data-state', pickupSelectable ? 'available' : (known ? 'unavailable' : 'unknown'));
      // The marker CSS styles the red line by, and the card click and the
      // `no-option` decision read. Written on every paint, so a new ZIP with
      // stores, or a variant that has them, takes it off again.
      if (noStores) pickupCard.setAttribute('data-im-no-stores', '');
      else pickupCard.removeAttribute('data-im-no-stores');
      if (pickupRadio) pickupRadio.disabled = !pickupSelectable;

      if (slot) {
        if (restricted) {
          slot.textContent = label('ship-only', 'Available for shipping or delivery only');
        } else if (pickupSelectable) {
          var store = (chosen && chosen.id) ? chosen : (data.pickup.nearestStore || null);
          var parts = [];
          /*
           * "Ready Tue, Oct 20 · FRISCO TX" (2026-10-17): the final pickup-ready
           * date the server computed, never a date of our own. The CHOSEN
           * store's own `readyDate` when this answer lists that store — the
           * date the cart will be written with (pickupFacts) — else the card's
           * `pickup.readyDate` (the nearest store's, which is what `pickup.eta`
           * has always described here), else the prose, reformatted from raw
           * 09/12/2026, which sat beside the shipping card's "Arrives Mon,
           * Sep 21" as two date formats in one control.
           */
          var listed = (chosen && chosen.id) ? storeInAnswer(data, chosen.id) : null;
          var when = listed
            ? pickupWhen(listed.readyDate, listed.availabilityMsg)
            : pickupWhen(data.pickup.readyDate, data.pickup.eta);
          if (when) parts.push(when);
          if (store && store.name) parts.push(store.name);
          slot.textContent = parts.join(' · ') || label('pdp-available', 'Available for pickup');
        } else if (noStores) {
          // The add gate's own words for it (Theme settings → In-store pickup →
          // "Pickup chosen, no store has it"), so the card and a refused press
          // can never say it two ways. Red by CSS, off [data-im-no-stores].
          slot.textContent = (root.getAttribute('data-im-gate-no-stores') || 'No stores near {zip} have this item.')
            .replace(/\{zip\}/g, noStores);
        } else if (!known) {
          slot.textContent = root.getAttribute('data-im-pickup-unknown-label') || 'Couldn’t check pickup right now';
        } else {
          // Only ever written into a HIDDEN card; kept true in case CSS is not.
          slot.textContent = label('unavailable', 'Pickup not available');
        }
      }
      if (trigger) {
        if (noStores) {
          // The ZIP is already in the sentence, so the link says what it does,
          // in the `no-option` line's words for the same act.
          trigger.textContent = root.getAttribute('data-im-none-zip-change') || 'Change ZIP';
          trigger.setAttribute('aria-label', zipChangeAria(noStores));
        } else {
          labelZipTrigger(trigger, zip);
        }
        // Re-attach if the slot write orphaned it.
        if (!trigger.isConnected && slot) slot.appendChild(trigger);
      }
    }

    /*
     * The middle card. Its radio VALUE is `delivery`, which §6 maps to
     * ship-to-home and placement turns into a Best Buy `shippingoption` lookup,
     * so it is a truthful control only when Best Buy will actually SHIP the SKU.
     *
     * HIDDEN WHENEVER A REAL ANSWER SAYS IT WILL NOT (2026-10-02) — `delivery:
     * null`, or a delivery-only `shipping: false` / `fulfillment: "home-delivery"`
     * answer. Until that day it was hidden only "when the shopper had somewhere
     * else to go" (the third card offered, or pickup selectable), because it was
     * the FALLBACK SELECTION: with the owner switch off, a delivery-only appliance
     * with no pickup kept this card — titled "Delivery", meaning ground shipping,
     * enabled and checked — so Add to Cart took an order that fails placement
     * (NO_SHIPPING_OPTION). The empty-picker problem that guard existed for is
     * now answered by `no-option`: the page says nothing can get the item there
     * and blocks the add. So the guard is gone. (Sku 6673091, which prompted the
     * 2026-09-25 hiding, answers shipping=false, delivery=true, pickup=true — no
     * ship-to-home route, so no ship-to-home card, then and now.)
     *
     * An unknown answer leaves it SHOWN and selectable — it is still Liquid's
     * default and the backend's own fallback, and "you can still order" has to
     * be true.
     */
    var shipVisible = known ? (verdict === 'answer' && shipOffered(data.delivery)) : true;

    if (deliveryCard) {
      var dslot = deliveryCard.querySelector('[data-im-delivery-state]');
      var shipLink = shipZipLink(deliveryCard);  // BEFORE any write to dslot detaches it
      paintDeliveryTitle(deliveryCard, known ? data.delivery : null);
      deliveryCard.hidden = !shipVisible;
      deliveryCard.setAttribute('data-state', known ? (shipVisible ? 'available' : 'unavailable') : 'unknown');
      if (shipRadio) shipRadio.disabled = !shipVisible;

      if (dslot) {
        if (!known) {
          dslot.textContent = root.getAttribute('data-im-unknown-label') ||
            'Couldn’t check right now — you can still order';
        } else if (shipVisible) {
          /*
           * `method` is not in this line because it is the card's TITLE
           * (paintDeliveryTitle, above) — printing it here as well is what once
           * rendered "Ground Shipping  Ground Shipping · 5-7 business days ·
           * FREE". `eta` is the whole detail line, verbatim: the endpoint owns
           * that string and pins it in tests.
           *
           * "FREE" is OUR policy, not `delivery.freeShipping`: free shipping on
           * orders of $150 or more, $8.99 below (GP, 2026-10-02 — reversing a
           * one-day misreading of 2026-10-01 that made every total free). So it
           * shows only when this item alone reaches the threshold
           * (qualifiesForFreeShipping). Best Buy's flag is its charge TO US, and
           * reading it left ~1,000 products with no "FREE" whatever our policy
           * said. This card is only ever shown for a parcel route now, so a home
           * delivery — never free, it carries the appliance delivery fee — cannot
           * reach this line; a SKU Best Buy will both ship and deliver is quoted
           * as shipping, and its parcel option IS free over the threshold. The
           * dagger (†; "*" until 2026-10-27, now the FICO® footnote's) points at
           * the free-shipping footnote in the SITE FOOTER
           * (#free-shipping-disclosure, sections/footer.liquid).
           */
          var parcelFree = qualifiesForFreeShipping(root);
          // A pre-order ships on its release date, whatever the parcel ETA says.
          var preorder = preorderEta(root);
          if (!preorder && !(zip && data.zip)) {
            paintShipZipPrompt(root, dslot, shipLink, parcelFree);
          } else {
            dslot.textContent = [preorder || deliveryDetailText(data.delivery), parcelFree ? 'FREE†' : '']
              .filter(Boolean).join(' · ');
          }
        } else {
          // Only ever written into a HIDDEN card.
          dslot.textContent = root.getAttribute('data-im-gate-no-delivery') ||
            'Shipping and delivery aren’t available for this item.';
        }
      }
    }

    /*
     * A hidden or disabled option must never be the selection (2026-10-02).
     * Disabling a radio does NOT uncheck it, and a disabled+checked radio still
     * matches input:checked — so it would keep reporting its value to the
     * add-to-cart re-assertion from a control the shopper cannot see.
     *
     * Two of the moves below WRITE, and both predate this rule: a restricted
     * pickup moves the cart to shipping (G4, 2026-09-15), and a dead shipping
     * card hands over to pickup (which writes only with a store remembered). The
     * third — a pickup this answer no longer offers, typically after a variant
     * switch — moves the radio only: the add re-asserts whatever is checked, and
     * the items already in the cart keep the fulfilment they were added under.
     *
     * When neither card is left, the group is left empty here and
     * improvemint-delivery.js's ensureSelection() moves it to Home Delivery inside
     * announceHomeDeliveryOffer below — or nothing is left at all, which is
     * `no-option`.
     */
    var checked = root.querySelector('input[type="radio"]:checked');
    if (checked && (checked === pickupRadio || checked === shipRadio) && !optionLive(checked)) {
      checked.checked = false;
      if (checked === pickupRadio && optionLive(shipRadio)) {
        shipRadio.checked = true;
        if (restricted) commitChoice('delivery', false);
      } else if (checked === shipRadio && optionLive(pickupRadio)) {
        pickupRadio.checked = true;
        commitChoice('store-pickup', false); // programmatic: never pop the drawer
      }
    }
    // An unknown answer cannot call on the third card (it is not announced, see
    // below), so the fallback is filled here: Shipping, which is live by then.
    if (!known && optionLive(shipRadio) && !root.querySelector('input[type="radio"]:checked')) {
      shipRadio.checked = true;
    }

    // After the cards are painted, so the third card's reveal cannot race the
    // second card's title. Only for an answer we actually got: an unknown one
    // says nothing about Home Delivery (see announceHomeDeliveryOffer).
    if (known) announceHomeDeliveryOffer(data.delivery);

    // After the reveal, because improvemint-delivery.js's ensureSelection() runs
    // inside it and can land on Pickup too. A shopper who closed the store drawer
    // without a store has said no to pickup; an answer arriving after that — the
    // move to pickup above included — does not get to say yes for them. See
    // leaveStorelessPickup.
    if (pickupDeclined) leaveStorelessPickup(root);

    /*
     * Last: is anything left to sell? AFTER the reveal above, because that is
     * what can still put a third option on the page — a SKU whose pickup and
     * shipping are both off is sellable while Best Buy will home-deliver it.
     */
    settleAvailability(root);
  }

  function loadPdp() {
    var root = pdpEl();
    if (!root) return;
    if (releaseState(root) === 'coming-soon') { showComingSoon(root); return; }
    var sku = root.getAttribute('data-im-sku');
    if (!sku) return;
    fetchOptions(sku, getZip()).then(function (data) {
      /*
       * Only if it is still the SKU on screen. A variant switch can leave an older
       * fetch in flight, and since 2026-10-02 an answer can BLOCK the sale — a
       * late answer for the previous variant must not stop the sale of this one.
       */
      if (root.getAttribute('data-im-sku') === sku) paintPdp(data);
    }, function () {
      /*
       * No answer at all. This used to leave the page exactly as it stood; it now
       * says so, in the same `unknown` words as an `error` answer (2026-10-02), so
       * a dead endpoint and a Best Buy outage read the same — "couldn't check
       * right now", never "not available", and the add still works.
       */
      if (root.getAttribute('data-im-sku') === sku) paintPdp(null);
    });
  }

  /* ---------------------------------------- ZIP and method before the add
   *
   * GP, 2026-10-28 — the product-page fulfilment gate. A press of Add to Cart on
   * a Best Buy product (one that renders this picker; every other product has no
   * [data-im-fulfillment] and never reaches this) is refused, with the reason on
   * the page, unless the shopper has given a ZIP, has an option selected, and that
   * option can reach that ZIP. Decided AT THE PRESS, in this order:
   *
   *   E  the sale is blocked (`unsellable`, `no-option`, `coming-soon`). The
   *      submit listener refuses that before the gate is asked, exactly as
   *      before; the gate never overrides a block and never makes one. The
   *      button stays clickable, and a refused press only says why.
   *   A  no ZIP (`im_bb_zip`): no add. The line above Add to Cart asks for a ZIP
   *      AND a choice, with the `no-option` line's "Enter ZIP" link. On a Home
   *      Delivery selection the date drawer also opens asking for the ZIP, as the
   *      press did before the gate (bookSoonest's no-ZIP path).
   *   B  a ZIP, and no option selected: no add; it asks for the choice only. Not
   *      the transient empty group mid-paint that §3b warns about: this is read
   *      at a press, after every paint, and blocks nothing standing.
   *   C  Pickup: allowed while the answer for this ZIP offers pickup — which
   *      store, and whether one is chosen, stay this file's existing business
   *      (choiceWriteForAdd). `pickup: null` refuses it, in red under the Pickup
   *      card: "No stores near {zip} have this item." for
   *      `pickupUnavailableReason: "no-stores-near-zip"`. Since the same day the
   *      painted answer keeps the card on the page saying exactly that
   *      (noStoresZip), radio disabled, so C is a press that beat the paint, and
   *      its words are the card's own line rather than a second red one.
   *   D  Shipping: allowed when Best Buy will ship it (shipOffered). Home
   *      Delivery: asks /apps/bestbuy/delivery for this SKU and ZIP, through
   *      improvemint-delivery.js's own lookup and 60 s cache
   *      (imDelivery.eligibleWindows), and refuses when `eligibleWindows` is
   *      empty, in red under the card — even with a window already in the cart,
   *      which was booked for another product or another day. `delivery: null`
   *      refuses either. NOT `delivery.earliestDate`: 6196700 at 75034 has none
   *      and 11 eligible windows.
   *   F  unknown is never a no. An `error` answer, `storesUnknown`, an /options
   *      or /delivery request that fails or outlasts GATE_WAIT_MS, a missing
   *      hook: the add goes on, and the checkout re-check
   *      (improvemint-cart-recheck.js) stays the backstop. A Home Delivery add
   *      with no window in the cart still needs bookSoonest() to book one before
   *      the line goes in (2026-10-17) — the gate letting a press through does
   *      not change what that step can and cannot write.
   *
   * WHY AT THE PRESS. The options answer is fetched on load, so A, B, C and
   * Shipping cost nothing; the Home Delivery question is a Best Buy call, which
   * only a shopper act may spend (F5/F13) — the press is one, a page view is not.
   * If the answer for this SKU and ZIP is not in hand yet (a ZIP typed a moment
   * ago, a variant just switched), the press waits for the request loadPdp
   * already made (fetchOptions joins it), bounded by GATE_WAIT_MS. So a press
   * costs at most ONE /apps/bestbuy/delivery lookup (Home Delivery only, and the
   * booking after it reads the same cache) and no /options request of its own —
   * except a retry when the page's own request had failed outright.
   *
   * RE-RUN, never re-asked. A refused press leaves its reason up; every repaint
   * (settleAvailability → applySaleBlock), a ZIP change and a change of option
   * re-read it from answers in hand (regate) and drop it once it no longer holds
   * or can no longer be known. A variant switch drops the answer-based ones
   * until the new answer lands. A quantity change is none of these and leaves it.
   *
   * ONE INTERCEPTION. gateAdd() is called from the capture-phase submit listener
   * below, after its sale-block refusal and before the cart write it makes; when
   * the gate had to wait, the press is re-sent through that same listener with
   * the gate stood aside (`gatePassed`), so the write, the booking and the replay
   * run exactly as they would have. The store drawer's "Add to cart & pick up
   * here" needs no gate: it exists only with a ZIP, a store from that ZIP's list
   * and pickup chosen. Copy: Theme settings → Improvemint Financial — In-store
   * pickup → "Before Add to Cart".
   */
  var GATE_WAIT_MS = 3000;
  var GATE_TIMEOUT = {};         // what within() resolves with when the wait ran out
  var GATE_PASS = { pass: true };
  var ASK_DATES = 'ask-dates';
  var NO_DATES = { reason: 'no-dates', option: 'home-delivery' };
  var gating = false;            // a press is waiting on the gate's question
  var gatePassed = false;        // that press, being re-sent after the gate let it go

  function within(promise, ms) {
    return new Promise(function (resolve, reject) {
      var t = setTimeout(function () { resolve(GATE_TIMEOUT); }, ms);
      Promise.resolve(promise).then(
        function (v) { clearTimeout(t); resolve(v); },
        function (err) { clearTimeout(t); reject(err); }
      );
    });
  }

  function sameAnswer(data, sku, zip) {
    return !!(data && String(data.sku) === String(sku) && (data.zip || '') === (zip || ''));
  }

  /* The options answer for exactly this SKU and ZIP — painted, else cached — or null. */
  function optionsFor(root, sku, zip) {
    if (sameAnswer(root.__imData, sku, zip)) return root.__imData;
    var cached = cacheGet('o:' + sku + ':' + zip);
    return sameAnswer(cached, sku, zip) ? cached : null;
  }

  /*
   * The option the press is about: the checked radio, when it is live. A Home
   * Delivery card on the page with no live selection is `unavailable` (no dates
   * for this ZIP the last time it asked), and that is the dates question again,
   * not "no option" — the existing add asks it again too (choiceWriteForAdd).
   */
  function gateMethod(root) {
    var checked = root.querySelector('input[type="radio"]:checked');
    if (checked && optionLive(checked)) return checked.value;
    var hd = root.querySelector('[data-im-fulfillment-option="home-delivery"]');
    return hd && !hd.hidden ? 'home-delivery' : '';
  }

  /* C and D from one answer: null to allow, a refusal, or ASK_DATES. */
  function gateDecide(method, data) {
    if (verdictOf(data) !== 'answer') return null;          // F, and `unsellable` is E's
    if (method === 'store-pickup') {
      if (data.pickup) return null;                         // C, `storesUnknown` included
      return {
        reason: data.pickupUnavailableReason === 'no-stores-near-zip' ? 'no-stores' : 'no-pickup',
        option: 'store-pickup'
      };
    }
    var d = data.delivery;
    if (!d) return { reason: 'no-delivery', option: method };
    if (method === 'home-delivery' && d.homeDelivery === true) return ASK_DATES;
    if (shipOffered(d)) return null;
    if (d.homeDelivery === true) return ASK_DATES;          // a truck-only SKU, whatever is checked
    return { reason: 'no-delivery', option: method };
  }

  function stillAbout(root, sku, zip) {
    return root.getAttribute('data-im-sku') === sku && getZip() === zip;
  }

  function afterOptions(root, method, data, sku, zip, peek) {
    var decided = gateDecide(method, data);
    if (!decided) return GATE_PASS;
    if (decided !== ASK_DATES) return { block: decided };
    var api = window.imDelivery;
    if (peek) {
      var seen = api && typeof api.peekEligibleWindows === 'function' ? api.peekEligibleWindows(sku, zip) : null;
      return seen && seen.known && seen.count === 0 ? { block: NO_DATES } : GATE_PASS;
    }
    if (!api || typeof api.eligibleWindows !== 'function') return GATE_PASS;
    return {
      wait: within(api.eligibleWindows(sku, zip), GATE_WAIT_MS).then(function (r) {
        if (r === GATE_TIMEOUT || !r || !r.known || !stillAbout(root, sku, zip)) return GATE_PASS;
        return r.count === 0 ? { block: NO_DATES } : GATE_PASS;
      }, function () { return GATE_PASS; })
    };
  }

  /*
   * The verdict: `{ pass }`, `{ block }`, or — at a press only — `{ wait }`, a
   * promise of one of the first two. `peek` (a re-run) never asks anybody; what
   * it cannot read from answers in hand is unknown, and unknown passes.
   */
  function gateCheck(root, peek) {
    var method = gateMethod(root);
    var zip = getZip();
    if (!zip) return { block: { reason: 'zip', method: method } };
    if (!method) return { block: { reason: 'method' } };
    var sku = root.getAttribute('data-im-sku');
    if (!sku) return GATE_PASS;
    var data = optionsFor(root, sku, zip);
    if (data) return afterOptions(root, method, data, sku, zip, peek);
    if (peek) return GATE_PASS;
    return {
      wait: within(fetchOptions(sku, zip), GATE_WAIT_MS).then(function (fresh) {
        if (fresh === GATE_TIMEOUT || !stillAbout(root, sku, zip)) return GATE_PASS;
        // loadPdp asked first and has painted this by now; if the gate asked first
        // (loadPdp's own request had failed), paint it here, so an `unsellable` or
        // `no-option` answer blocks the sale (E) instead of being decided past.
        if (!sameAnswer(root.__imData, sku, zip)) paintPdp(fresh);
        if (root.hasAttribute('data-im-sale-blocked')) return GATE_PASS;
        var after = afterOptions(root, method, fresh, sku, zip, false);
        return after.wait || after;
      }, function () { return GATE_PASS; })
    };
  }

  function gateWords(root, g) {
    var words;
    if (g.reason === 'zip') {
      words = root.getAttribute('data-im-gate-zip-method') ||
        'Enter your ZIP code and choose pickup or delivery to add this item to your cart.';
    } else if (g.reason === 'method') {
      words = root.getAttribute('data-im-gate-method') ||
        'Choose pickup or delivery to add this item to your cart.';
    } else if (g.reason === 'no-stores') {
      words = root.getAttribute('data-im-gate-no-stores') || 'No stores near {zip} have this item.';
    } else if (g.reason === 'no-pickup') {
      words = label('unavailable', 'Pickup not available');
    } else if (g.reason === 'no-dates') {
      words = root.getAttribute('data-im-gate-no-dates') || 'No delivery dates are available for {zip}.';
    } else {
      words = root.getAttribute('data-im-gate-no-delivery') ||
        'Shipping and delivery aren’t available for this item.';
    }
    return words.replace(/\{zip\}/g, getZip() || '');
  }

  /*
   * Say it, or stop saying it. An option's reason goes in red under that card
   * (its [data-im-option-error]); A, B and any reason whose card is not on the
   * page go in the line above Add to Cart, which is already red. The note is left
   * to a sale block whenever one stands. `pressed` brings the words into view: a
   * press from the sticky bar can leave the picker off screen, and a button that
   * did nothing visible reads as broken.
   */
  function showGate(root, g, pressed) {
    root.__imGate = g || null;
    var card = g && g.option ? root.querySelector('[data-im-fulfillment-option="' + g.option + '"]') : null;
    var slot = card && !card.hidden ? card.querySelector('[data-im-option-error]') : null;
    var words = g ? gateWords(root, g) : '';
    // "No stores near {zip}" is already the Pickup card's own line once that
    // ZIP's answer is painted (2026-10-28): said once, under the card, not twice.
    var cardSays = !!(slot && g.reason === 'no-stores' && card.hasAttribute('data-im-no-stores'));
    var slots = root.querySelectorAll('[data-im-option-error]');
    for (var i = 0; i < slots.length; i++) {
      var want = slots[i] === slot && !cardSays ? words : '';
      if (slots[i].textContent !== want) slots[i].textContent = want;
    }
    if (!root.hasAttribute('data-im-sale-blocked')) {
      if (g && !slot) {
        writeNote(root, words, g.reason === 'zip' ? {
          text: root.getAttribute('data-im-none-zip-enter') || 'Enter ZIP',
          aria: label('zip-aria-enter', 'Enter a ZIP code')
        } : null);
      } else {
        writeNote(root, '', null);
      }
    }
    if (!pressed || !g) return;
    var shown = (cardSays && card) || slot || document.querySelector('[data-im-fulfillment-note]');
    if (shown && shown.scrollIntoView) {
      try { shown.scrollIntoView({ block: 'nearest' }); } catch (e) { /* cosmetic */ }
    }
  }

  /* A sale block took over: no red line of ours stays under it. The note is the block's. */
  function dropGate(root) {
    if (!root.__imGate) return;
    root.__imGate = null;
    var slots = root.querySelectorAll('[data-im-option-error]');
    for (var i = 0; i < slots.length; i++) if (slots[i].textContent) slots[i].textContent = '';
  }

  /* Re-read a refused press's reason from answers in hand. Called whenever the
   * note is re-settled with no sale block, so it also writes the empty note. */
  function regate(root) {
    var g = root.__imGate;
    if (g) g = gateCheck(root, true).block || null;
    showGate(root, g, false);
  }

  function resendAdd(form, submitter) {
    gatePassed = true;
    try {
      if (form.requestSubmit) {
        try { form.requestSubmit(submitter); } catch (err) { form.requestSubmit(); }
      } else if (submitter && submitter.click) {
        submitter.click();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    } finally {
      gatePassed = false;
    }
  }

  /*
   * The press, from the submit listener. True when the gate took it (refused it,
   * or is holding it for an answer); false to let the listener carry on.
   */
  function gateAdd(e, form, root) {
    if (gatePassed || bookingForAdd) return false;    // re-sent by us; or a booking's own swallow
    if (gating) { e.preventDefault(); e.stopPropagation(); return true; }

    var r = gateCheck(root, false);
    if (r.pass) {
      if (root.__imGate) showGate(root, null, false);
      return false;
    }
    e.preventDefault();
    e.stopPropagation();                               // keep it from Hyper's ProductForm
    if (r.block) {
      showGate(root, r.block, true);
      if (r.block.reason === 'zip' && r.block.method === 'home-delivery' &&
          window.imDelivery && typeof window.imDelivery.askZip === 'function') {
        window.imDelivery.askZip();
      }
      return true;
    }

    var submitter = e.submitter || null;
    gating = true;
    function settle(res) {
      gating = false;
      if (root.hasAttribute('data-im-sale-blocked')) {   // E, decided while we waited
        applySaleBlock(root, root.getAttribute('data-im-sale-blocked'));
        return;
      }
      if (res && res.block) { showGate(root, res.block, true); return; }
      if (root.__imGate) showGate(root, null, false);
      // In a new task, for the reason the hold in the submit listener gives.
      setTimeout(function () { resendAdd(form, submitter); }, 0);
    }
    r.wait.then(settle, function () { settle(GATE_PASS); });
    return true;
  }

  // A ZIP typed into either drawer, or another option picked, re-reads a reason
  // left up by a refused press. Neither asks Best Buy anything.
  document.addEventListener('im:zip-change', function () {
    var root = pdpEl();
    if (root && root.__imGate) regate(root);
  });
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest('[data-im-fulfillment] input[type="radio"]')) return;
    var root = pdpEl();
    if (root && root.__imGate) regate(root);
  });

  /*
   * ------------------------------------------------- following the variant picker
   *
   * Liquid puts the SELECTED variant's Best Buy SKU in `data-im-sku` (see
   * snippets/improvemint-bb-sku.liquid — the variant's sku wins, `bb_sku` is only
   * the fallback, because that metafield is product-level and last-writer-wins
   * across the siblings consolidate-variants merges into one product). On a PDP
   * with a picker that is only the FIRST answer: 6,767 SKUs sit behind a variant
   * picker, and switching variant must change the SKU this widget asks about, or
   * the fix would hold only until the shopper touched the picker.
   *
   * Hyper does not re-render this fieldset on a variant change. `product-info`
   * re-fetches the section and then copies over a fixed list of subtrees — price,
   * Sku, Barcode, Inventory, Badges, PricePerItem, Volume, media, quantity rules,
   * size chart, the add button (assets/product-info.js,
   * `updateSourceFromDestination`). This fieldset is not on that list, so nothing
   * would ever rewrite `data-im-sku`. The VARIANT picker is a separate story —
   * `updateOptionValues` replaces the whole `variant-selects` element, which is
   * what lets improvemint-variant-picker.js compute its sparse-option matrix in
   * Liquid and get a fresh answer on every change. That does not help here: this
   * fieldset sits outside that element.
   *
   * The new SKU comes from [data-im-variant-index], which Liquid rendered from the
   * same resolver, one entry per variant. So the RULE is not duplicated here: this
   * is a lookup, not a second implementation of "is this sku numeric".
   *
   * Trigger: the bubbling `change` that `updateVariantInputs` fires on
   * `input[name="id"]`, NOT the `variant:changed` custom event. Two reasons, both
   * checked in assets/product-info.js:
   *
   *   `variant:changed` carries `detail.variant: this.currentVariant`, and
   *   `currentVariant` is assigned on connect and on the product-SWAP path only —
   *   never in `handleUpdateProductInfo`. On an ordinary variant switch its detail
   *   is therefore the PREVIOUS variant.
   *
   *   `updateVariantInputs` runs before the early `return` for an unavailable
   *   option combination, where `variant:changed` is never dispatched at all.
   *
   * A quick-add form on a product card elsewhere on the page carries `name="id"`
   * too. It cannot move this widget: its variant id is not in the PDP's index, so
   * the lookup misses and nothing happens.
   */
  function variantSkuFor(root, variantId) {
    var index = root.querySelector('[data-im-variant-index]');
    if (!index || !variantId) return null;
    var nodes = index.querySelectorAll('[data-im-variant-id]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-im-variant-id') === String(variantId)) {
        return nodes[i].getAttribute('data-im-variant-sku') || null;
      }
    }
    return null;
  }

  /*
   * Does THIS item, on its own, reach the free-shipping threshold? (GP,
   * 2026-10-02: "orders are ONLY FREE over $150" — $8.99 below it.) The page
   * cannot see the rest of the cart, so it only promises what one unit of the
   * selected variant earns by itself; a cheaper item shows no "FREE" and the
   * cart's free-shipping bar takes over from there.
   *
   * Price: the selected variant's from [data-im-variant-index] when there is a
   * picker, else the fieldset's own `data-im-product-price-cents` (the variant the
   * page opened on). Threshold 0 or unreadable = never free — fail towards not
   * promising, because checkout charges the $8.99 whatever the page says.
   */
  function qualifiesForFreeShipping(root) {
    var threshold = parseInt(root.getAttribute('data-im-free-threshold-cents'), 10);
    if (!(threshold > 0)) return false;
    var price = NaN;
    var variantId = pdpSelectedVariantId(root);
    var index = root.querySelector('[data-im-variant-index]');
    if (index && variantId) {
      var nodes = index.querySelectorAll('[data-im-variant-id]');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].getAttribute('data-im-variant-id') === String(variantId)) {
          price = parseInt(nodes[i].getAttribute('data-im-variant-price'), 10);
          break;
        }
      }
    }
    if (isNaN(price)) price = parseInt(root.getAttribute('data-im-product-price-cents'), 10);
    return price >= threshold;
  }

  /*
   * Scoped to the picker's own `product-info`, not `document`: a PDP holds other
   * cart-add forms (recommendations, recently viewed, the drawer), and the first
   * one in the document is not guaranteed to be this product's.
   */
  function pdpSelectedVariantId(root) {
    var scope = (root.closest && root.closest('product-info')) || document;
    var input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
    return input && input.value ? String(input.value) : null;
  }

  /*
   * The old answer is left on the cards until the new one lands, exactly as a
   * failed fetch is. Repainting to `unknown` first would be worse than a brief
   * stale line: paintPdp disables radios and can move the selection, so a
   * placeholder paint would write cart attributes for a state nobody chose.
   */
  function syncPdpSku() {
    var root = pdpEl();
    if (!root) return;
    var sku = variantSkuFor(root, pdpSelectedVariantId(root));
    if (!sku || sku === root.getAttribute('data-im-sku')) return;
    root.setAttribute('data-im-sku', sku);
    root.__imData = null;
    /*
     * The sale block is the ONE thing that is dropped rather than left standing,
     * and for the opposite reason to the cards above: a stale line is a cosmetic
     * wrong, but a stale block is one variant's verdict stopping the sale of
     * another. "Not known" never blocks, and a variant we have not asked about
     * yet is exactly that — so the button goes back to whatever Liquid and Hyper
     * say until the new answer lands, which is how this product behaved before
     * any of this existed. paintPdp re-applies it within the same fetch. The
     * reason line and the stock-line override go with it (2026-10-02).
     */
    applySaleBlock(root, releaseState(root) === 'coming-soon' ? 'coming-soon' : null);
    loadPdp();
  }

  /* ---------------------------------------------------------------- cart */

  /*
   * The whole cart carries ONE fulfillment, as cart ATTRIBUTES — not line-item
   * properties. deriveFulfillmentChoice in the commerce app requires a
   * non-empty bb_store_id to treat a cart as pickup, and falls back to
   * ship-to-home otherwise, so never write store-pickup without a store id.
   */
  function updateCart(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { /* non-fatal: the PDP choice is re-asserted on add */ });
  }

  /*
   * Tell the cart surfaces what was just written.
   *
   * The mini-cart is global, so its Liquid was rendered before the shopper
   * touched this picker, and Hyper re-renders the cart section on an add or a
   * quantity change but NOT on a bare /cart/update.js. Without this, changing the
   * radio and then opening the drawer shows the previous choice.
   *
   * A statement of fact, not a request: assets/improvemint-cart-fulfillment.js
   * repaints the banner from it and re-fetches nothing. Liquid is still the
   * source of truth on the next render — this only stops the banner disagreeing
   * with the radio the shopper is looking at.
   */
  function announceCart(state, value) {
    document.dispatchEvent(new CustomEvent('im:cart-fulfillment-change', {
      detail: { state: state, value: value || '' }
    }));
  }

  /*
   * The picker's server-rendered view of what the CART holds, kept true after
   * our own writes.
   *
   * Liquid puts it on the fieldset (data-im-cart-fulfillment + the five delivery
   * keys) and improvemint-delivery.js has always updated it after ITS writes
   * (rememberCart there) — this file did not, so after a pickup write the
   * fieldset could still claim a home-delivery window that the write had just
   * deleted. cartWindow() in improvemint-delivery.js reads exactly those
   * attributes, and so does assertChoiceBeforeAdd() below, so the mirror has to
   * be true for both.
   *
   * Only the delivery keys are blanked here, never written: a window is
   * improvemint-delivery.js's to record, and the two values this file writes
   * (`store-pickup`, `delivery`) both delete every delivery key from the cart.
   */
  function rememberCart(fulfillment, storeId, readyDate) {
    var root = pdpEl();
    if (!root) return;
    root.setAttribute('data-im-cart-fulfillment', fulfillment || '');
    root.setAttribute('data-im-cart-store-id', storeId || '');
    root.setAttribute('data-im-cart-pickup-ready-date', readyDate || '');
    root.setAttribute('data-im-cart-delivery-date', '');
    root.setAttribute('data-im-cart-delivery-start', '');
    root.setAttribute('data-im-cart-delivery-end', '');
    root.setAttribute('data-im-cart-delivery-zip', '');
    root.setAttribute('data-im-cart-delivery-earliest', '');
  }

  function cartFulfillment(root) {
    return (root.getAttribute('data-im-cart-fulfillment') || '').trim();
  }

  /* ------------------------------------- the pickup date and address (2026-10-17)
   *
   * Two DISPLAY-ONLY cart attributes ride beside the eleven on every pickup write,
   * and are written "" on every other write (both clears included):
   *
   *   bb_pickup_ready_date  YYYY-MM-DD — the chosen store's `readyDate` from
   *                         /apps/bestbuy/options: Best Buy's pickup day + our
   *                         pickup lead time, computed server-side. The cart line
   *                         says "Ready Tue, Oct 20" from it, and the thank-you /
   *                         order-status block "Ready for pickup: Tuesday,
   *                         October 20" (apps/bestbuy-sync/extensions/
   *                         pickup-confirmation/src/pickup-display.ts).
   *   bb_store_address      one display line, "3333 Preston Rd, Frisco, TX 75034".
   *
   * Placement never reads either: deriveFulfillmentChoice (order-mapping.ts) reads
   * named keys — bb_fulfillment, bb_store_id, bb_store_name — and nothing else, so
   * these cannot change what Best Buy is sent (docs/COMMERCE-PDP-PICKER.md §6).
   *
   * WHERE THE DATE COMES FROM. `readyDate` is per SKU AND per store, so it is read
   * from the options answer for the product on this page, at write time — never
   * from localStorage, which remembers a store across products and days. Only that
   * store's OWN date is written: a store the answer does not list gets "", because
   * another store's date is a promise about the wrong counter.
   *
   * ONE DATE FOR A CART OF SEVERAL ITEMS. The cart carries one pickup, so one date,
   * and the order is ready when its LAST item is. So when the cart is already on
   * pickup at this same store, the later of the cart's date and this item's is
   * written — "" if this item's is unknown, because then the order's is too. It
   * can err late (an item removed afterwards leaves its date standing), never
   * early: a pickup promised before the store has everything is the one wrong
   * answer.
   */

  /* The answer for the SKU and ZIP on this page right now, or null. */
  function answerFor(data, sku, zip) {
    return !!(data && data.pickup && sku && String(data.sku) === String(sku) && (data.zip || '') === (zip || ''));
  }

  function storeInAnswer(data, storeId) {
    var stores = (data && data.pickup && data.pickup.stores) || [];
    for (var i = 0; i < stores.length; i++) {
      if (stores[i] && String(stores[i].id) === String(storeId)) return stores[i];
    }
    return null;
  }

  /*
   * This product's answer for the ZIP in hand: the painted one, else the
   * sessionStorage copy (a variant switch clears the painted one until the new
   * answer lands). Matched on the answer's own `sku` and `zip`, so a late answer
   * for another variant or an old ZIP is never read as this one.
   */
  function pdpAnswer() {
    var root = pdpEl();
    if (!root) return null;
    var sku = root.getAttribute('data-im-sku');
    var zip = getZip();
    if (answerFor(root.__imData, sku, zip)) return root.__imData;
    var cached = sku ? cacheGet('o:' + sku + ':' + (zip || '')) : null;
    return answerFor(cached, sku, zip) ? cached : null;
  }

  /* "3333 Preston Rd, Ste 200, Frisco, TX 75034" — whatever parts the Stores API sent. */
  function addressLine(s) {
    if (!s) return '';
    var street = [s.address, s.address2].filter(function (v) { return v && String(v).trim(); }).join(', ');
    var place = [s.city, [s.region, s.postalCode].filter(Boolean).join(' ')]
      .filter(function (v) { return v && String(v).trim(); }).join(', ');
    return [street, place].filter(Boolean).join(', ').trim();
  }

  /*
   * What a pickup write at `store` carries in the two display keys. `store` is the
   * remembered `{ id, name, address }` (chooseStore).
   */
  function pickupFacts(store) {
    var root = pdpEl();
    var listed = store && store.id ? storeInAnswer(pdpAnswer(), store.id) : null;
    var ready = listed ? validReadyDate(listed.readyDate) : '';
    if (ready && root && cartFulfillment(root) === 'store-pickup' &&
        (root.getAttribute('data-im-cart-store-id') || '') === String(store.id)) {
      var had = validReadyDate(root.getAttribute('data-im-cart-pickup-ready-date'));
      if (had > ready) ready = had;   // YYYY-MM-DD compares as text
    }
    return {
      readyDate: ready,
      address: addressLine(listed) || (store && store.address) || ''
    };
  }

  /*
   * The same completeness test improvemint-delivery.js's cartWindow() applies,
   * and for the same reason: a home-delivery choice counts only when the cart
   * carries a date AND a start time, because placement fails half a window
   * (DELIVERY_WINDOW_MISSING) rather than shipping it.
   */
  function cartHasDeliveryWindow(root) {
    return cartFulfillment(root) === 'home-delivery' &&
      !!(root.getAttribute('data-im-cart-delivery-date') || '').trim() &&
      !!(root.getAttribute('data-im-cart-delivery-start') || '').trim();
  }

  /*
   * `fromUser` guards the modal. commitChoice runs both from a real click on a
   * radio AND from paintPdp auto-selecting pickup on a SKU that cannot ship —
   * and the pickup branch opens the store picker when no store is chosen yet.
   * Without the guard, landing on a pickup-only PDP throws the drawer open and
   * locks page scroll before the shopper has done anything. Measured, not
   * theorised: the harness came up with the drawer already open.
   *
   * RETURNS the /cart/update.js promise when it wrote, and `null` when it did
   * not — the add-to-cart re-assertion below has to wait for the write before it
   * lets the line be added, and has to know when there was nothing to wait for.
   */
  function commitChoice(choice, fromUser) {
    // Home Delivery belongs to improvemint-delivery.js, which is listening for
    // the same change event. Falling through here would write
    // `bb_fulfillment: 'delivery'` — ship-to-home — over the shopper's chosen
    // delivery window, because the `else` below treats every non-pickup value
    // as shipping.
    if (choice === 'home-delivery') return null;

    var zip = getZip();
    var store = getStore();
    var root = pdpEl();
    var data = root && root.__imData;

    if (choice === 'store-pickup') {
      if (!store || !store.id) {
        // No store = no pickup. deriveFulfillmentChoice needs a non-empty
        // bb_store_id or it silently falls back to ship-to-home, so write
        // nothing rather than write a broken pickup. The selection is only
        // provisional until a store is chosen: closing the drawer without one
        // puts it back (closeModal → leaveStorelessPickup).
        if (fromUser) openModal(root);
        return null;
      }
      // Read BEFORE rememberCart moves the mirror: pickupFacts keeps the later of
      // the cart's date and this item's, and needs the cart's as it stands.
      var facts = pickupFacts(store);
      rememberCart('store-pickup', String(store.id), facts.readyDate);
      var pickupWrite = updateCart({
        bb_fulfillment: 'store-pickup',
        bb_zip: zip || '',
        bb_store_id: String(store.id),
        bb_store_name: store.name || '',
        bb_shipping_option: '',
        bb_delivery_date: '',
        bb_delivery_start: '',
        bb_delivery_end: '',
        bb_delivery_zip: '',
        bb_delivery_earliest: '',
        bb_delivery_services: '',
        bb_pickup_ready_date: facts.readyDate,
        bb_store_address: facts.address
      });
      announceCart('store-pickup', store.name || '');
      return pickupWrite;
    }

    rememberCart('delivery');
    var shipWrite = updateCart({
      bb_fulfillment: 'delivery',
      bb_zip: zip || '',
      bb_store_id: '',
      bb_store_name: '',
      bb_shipping_option: (data && data.delivery && data.delivery.shippingOptionKey) || '',
      bb_delivery_date: '',
      bb_delivery_start: '',
      bb_delivery_end: '',
      bb_delivery_zip: '',
      bb_delivery_earliest: '',
      bb_delivery_services: '',
      bb_pickup_ready_date: '',
      bb_store_address: ''
    });
    announceCart('delivery', '');
    return shipWrite;
  }

  /* ------------------------------------------- the cart gets what was SELECTED
   *
   * Until 2026-09-25 a cart attribute was written on ONE event: `change` on a
   * radio. That is not the same thing as "the choice the shopper is looking at",
   * and the gap is a placement bug rather than a cosmetic one. GP, live:
   *
   *   "i noticed that once i selected a pickup in store, then I deleted the cart
   *    and selected ground shipping, cart stays stuck on pickup in store."
   *
   * Three things had to line up for that, and only the first is about emptying
   * the cart (see clearEmptyCart() in improvemint-cart-fulfillment.js). The other
   * two are this function's:
   *
   *   Liquid renders the middle card `checked` for every cart that is not a
   *   complete home delivery — the correct default for a shippable SKU, and it
   *   says nothing about a cart that is on store pickup. So the picker can read
   *   "Ground Shipping" over a cart that says `store-pickup`, and does, on the
   *   first PDP after a pickup.
   *
   *   Clicking that already-checked radio fires NO `change` event, so the one
   *   act that should have fixed it wrote nothing. The shopper does the obvious
   *   thing, sees the right answer on screen, and gets a store pickup.
   *
   * So the rule is now: WHAT THE SHOPPER SEES SELECTED IS WHAT THE CART GETS, at
   * the one moment it can be settled without guessing — the add. And if the
   * selected option cannot be written COMPLETELY, the cart's fulfilment is
   * cleared rather than left carrying somebody else's choice — EXCEPT Home
   * Delivery (2026-10-17), where a cleared cart is itself unplaceable: that add
   * books the soonest window first, or does not happen (bookForAdd).
   *
   * WHY NOT ON PDP LOAD. Reconciling at render time means either writing the
   * cart with no shopper act — which would throw away a pickup deliberately
   * chosen one product ago, just by browsing the next PDP — or checking the
   * pickup radio and naming a store on a product nobody picked up. Viewing a PDP
   * is not a selection (§6: last PDP SELECTION wins). Adding to it is.
   *
   * IT SPENDS NO BEST BUY CALL — with one exception since 2026-10-17 (the add
   * gate that runs before it, 2026-10-28, keeps its own count above). Everything
   * below is read from the DOM, from localStorage, and from the options answer
   * already fetched on load; the only request it can make is /cart/update.js.
   * That matters: F5/F13 rule out a lookup on anything but a deliberate act on one
   * product, and a cart write is not a lookup. The exception is Home Delivery
   * with no window in the cart, where the press of Add to Cart IS that deliberate
   * act and one /apps/bestbuy/delivery lookup books the window (bookForAdd).
   */

  /*
   * Give the cart back no fulfilment at all. All eleven keys and the two pickup
   * display keys, `bb_zip` kept —
   * the same split clearEmptyCart() makes, for the same reason: the ZIP is where
   * the shopper is, not what they chose, and with `bb_fulfillment` absent
   * `fulfillmentProps` reads nothing, so it cannot reach an order on its own.
   *
   * "No fulfilment" is a truthful state, not a broken one: placement resolves a
   * shipping option live for a cart with no choice (pipeline.ts's `else` branch),
   * exactly as it does for `bb_fulfillment: delivery`.
   */
  function clearChoice() {
    rememberCart('');
    announceCart('delivery', ''); // what Liquid renders for a blank attribute
    return updateCart({
      bb_fulfillment: '',
      bb_zip: getZip() || '',
      bb_store_id: '',
      bb_store_name: '',
      bb_shipping_option: '',
      bb_delivery_date: '',
      bb_delivery_start: '',
      bb_delivery_end: '',
      bb_delivery_zip: '',
      bb_delivery_earliest: '',
      bb_delivery_services: '',
      bb_pickup_ready_date: '',
      bb_store_address: ''
    });
  }

  /*
   * Is this the form for the product the picker is about?
   *
   * A PDP carries other cart-add forms — recommendations, recently viewed, the
   * drawer — and writing this product's fulfilment for one of those would be the
   * same class of lie in the other direction. The sticky add-to-cart bar's form
   * is a legitimate one: it is a second control for the same product and syncs
   * the same variant id.
   *
   * Matched on the variant, not the DOM position, for that reason. The index is
   * the one Liquid renders for a multi-variant product; on a single-variant PDP
   * there is none, and the exact match against the picker's own form answers it.
   */
  function isThisProductsAddForm(form, root) {
    var input = form.querySelector('[name="id"]');
    var value = input && input.value ? String(input.value) : '';
    if (!value) return false;
    var mine = pdpSelectedVariantId(root);
    if (mine && value === mine) return true;
    return !!variantSkuFor(root, value);
  }

  /*
   * Home Delivery with no window in the cart: BOOK ONE, OR DO NOT ADD (2026-10-17).
   *
   * Returns `{ hold: Promise<boolean> }` rather than a write: the listener below
   * adds the line only if it resolves true, and never on a timer — an add that
   * lands before the window is exactly the bug this exists to stop.
   *
   * improvemint-delivery.js owns windows, so it does the booking
   * (`window.imDelivery.bookSoonest`): with a ZIP it looks up the soonest window
   * and writes it the way selecting the card does; with no ZIP it opens the date
   * drawer to ask for one; with no window on offer it says "No delivery dates in
   * the next 14 days". In every case but a booked window the add does not happen,
   * and the reason goes in the line above Add to Cart as well as on the card —
   * the card may be out of view, and the shopper just pressed a button that did
   * nothing visible. A sale block already on the page keeps its own words.
   *
   * No hook at all (the delivery script failed to load) means no add either. It
   * is unreachable in practice — without that script the Home Delivery card is
   * never revealed, so it can never be the selection — and if it ever happened,
   * adding a truck-only item with no window is the outcome this replaces.
   */
  function bookForAdd(root) {
    var api = window.imDelivery;
    if (!api || typeof api.bookSoonest !== 'function') return { hold: Promise.resolve(false) };
    return {
      hold: Promise.resolve(api.bookSoonest()).then(function (r) {
        var speak = !root.hasAttribute('data-im-sale-blocked');
        if (r && r.ok) {
          if (speak) writeNote(root, '', null);
          return true;
        }
        if (speak) writeNote(root, (r && r.text) || '', null);
        return false;
      }, function () { return false; })
    };
  }

  /*
   * The write this add needs, or null when the cart already agrees and the add
   * can go straight through — or `{ hold }` (bookForAdd) when the add has to wait
   * for a delivery window and may not happen at all.
   */
  function choiceWriteForAdd(root) {
    var checked = root.querySelector('input[type="radio"]:checked');
    // A hidden or disabled option is not a choice the shopper can see, so it is
    // not one the cart may get (2026-10-02). paintPdp and ensureSelection() never
    // leave one checked; this is the guard that does not depend on that.
    if (checked && !optionLive(checked)) checked = null;
    var value = checked ? checked.value : '';
    var have = cartFulfillment(root);

    if (value === 'home-delivery') {
      /*
       * Only improvemint-delivery.js can write a window, and only a COMPLETE one
       * is placeable. If the cart already holds it the cart is already right and
       * this is a no-op.
       *
       * If it does not — the radio was auto-selected by ensureSelection() on a
       * delivery-only appliance, or a window was cleared off an emptied cart —
       * the add BOOKS the soonest window first, or does not happen (2026-10-17).
       *
       * *This used to clear the fulfilment instead, reasoning that "such an order
       * is unplaceable either way" and that a lookup here would be a Best Buy call
       * the shopper did not ask for (F5).* The first half was the defect: the
       * cleared cart carried only `bb_zip`, checkout offered "Standard — FREE",
       * and placement failed ship-to-home for a truck-only SKU — on every
       * delivery-only appliance, because ensureSelection() auto-selects this card
       * on all of them and that fires no `change`. Reproduced on the live theme,
       * Fisher & Paykel dishwasher sku 6196700. The second half does not hold for
       * a press of Add to Cart: it is a deliberate act on this one product, it
       * costs one lookup behind the same 60 s cache, and it is the lookup selecting
       * the card would have made (improvemint-delivery.js lists it as the fifth
       * caller). What still must not happen is the order going out as somebody
       * else's store pickup — and it cannot: a booked window replaces every key.
       */
      return cartHasDeliveryWindow(root) ? null : bookForAdd(root);
    }

    if (value === 'store-pickup') {
      // deriveFulfillmentChoice silently SHIPS a pickup carrying no store id, so
      // a storeless pickup is not a choice we can write at all. And a cart on
      // pickup at ANOTHER store is not this selection either — hence the store
      // id in the mirror, which is the only reason it is rendered.
      var store = getStore();
      if (!store || !store.id) return clearChoice();
      /*
       * Same store AND the same pickup date. The date is per item (2026-10-17):
       * adding a second product at the same store can move the order's ready
       * date later (pickupFacts keeps the later one), and that is a write even
       * though the store already matches. The common case — same product, or a
       * product ready the same day — still costs no request.
       */
      if (have === 'store-pickup' &&
          (root.getAttribute('data-im-cart-store-id') || '') === String(store.id) &&
          pickupFacts(store).readyDate === validReadyDate(root.getAttribute('data-im-cart-pickup-ready-date'))) return null;
      // commitChoice, not a second copy of the write: it is the one place that
      // knows what a pickup write contains. `false` because this is not the
      // shopper reaching for the radio — it must never pop the store drawer.
      return commitChoice('store-pickup', false);
    }

    if (value === 'delivery') {
      if (have === 'delivery') return null;   // the common case: no extra request
      return commitChoice('delivery', false);
    }

    /*
     * Nothing checked. The picker is showing the shopper no choice, so the cart
     * must not be carrying one behind it. ensureSelection() makes this all but
     * unreachable; it is here because "the cart may hold only what the picker
     * shows" is the whole rule, and an empty group shows nothing.
     *
     * One way to get here IS reachable (2026-10-17): a delivery-only appliance
     * whose Home Delivery card is on the page but `unavailable` — Best Buy had no
     * window for this ZIP, so its radio was disabled and unchecked and nothing
     * else is live. Clearing and adding there is the same unplaceable
     * ship-to-home order as above, so it goes the same way: try to book (the
     * dates may have opened up since), else no add and the "no dates" words.
     */
    var hd = root.querySelector('[data-im-fulfillment-option="home-delivery"]');
    if (hd && !hd.hidden) return bookForAdd(root);
    return have ? clearChoice() : null;
  }

  /*
   * Hyper's ProductForm listens for `submit` on the form itself and POSTs
   * /cart/add.js, so a listener on `document` in the CAPTURE phase is the one
   * place that can get in front of it without touching theme.js. The attribute
   * write goes first and the submit is replayed after it settles — the same
   * ordering, and the same reason, as addToCartWithPickup: an add that lands
   * before the attributes is an item sitting under the wrong fulfilment.
   *
   * It can never strand the button. The replay is fired by whichever comes
   * first, the write settling or REPLAY_AFTER_MS, and `replaying` lets the
   * second submit through untouched.
   */
  var REPLAY_AFTER_MS = 2500;
  var replaying = false;
  /*
   * A delivery window is being booked for an add (bookForAdd). A second press
   * meanwhile is swallowed: it would start a second booking and, once both
   * resolve, add the line twice. Released when the booking settles, whichever way.
   */
  var bookingForAdd = false;

  document.addEventListener('submit', function (e) {
    if (replaying) return;

    var form = e.target;
    if (!form || !form.getAttribute) return;
    if (!/\/cart\/add\b/.test(form.getAttribute('action') || '')) return;

    var root = pdpEl();
    if (!root) return;                                  // no picker: not our add
    if (!isThisProductsAddForm(form, root)) return;

    /*
     * Nothing can fulfil this SKU, so there is nothing to add. The button is
     * already disabled (applySaleBlock), and this is the guard that does not
     * depend on it staying that way: Hyper re-renders the add button on a variant
     * change and the sticky bar has a button of its own, so the DOM can hand us
     * back a live control. Refuse in CAPTURE, in front of Hyper's ProductForm, so
     * no /cart/add.js request is made at all, and re-assert the block on the way
     * out in case that is what revived it.
     *
     * No cart write here: an add that is not happening must not move the
     * fulfilment the cart already carries for the items in it.
     */
    if (root.hasAttribute('data-im-sale-blocked')) {
      e.preventDefault();
      e.stopPropagation();
      applySaleBlock(root, root.getAttribute('data-im-sale-blocked'));
      return;
    }
    // A ZIP, a choice, and a choice that reaches the ZIP (2026-10-28) — see
    // "ZIP and method before the add". After the block above, which it never overrides.
    if (gateAdd(e, form, root)) return;

    if (bookingForAdd) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    var write = choiceWriteForAdd(root);
    if (!write) return;                                 // the cart already agrees

    e.preventDefault();
    e.stopPropagation();                                // keep it from the form's own listener

    var submitter = e.submitter || null;
    var replayed = false;
    function replay() {
      if (replayed) return;
      replayed = true;
      replaying = true;
      try {
        /*
         * requestSubmit re-dispatches a real submit event, which is what Hyper
         * is listening for; passing the submitter keeps `event.submitter`, which
         * ProductForm reads. It throws if the button is not a submit control of
         * this form, so that falls back to a plain requestSubmit, and the last
         * resort is .submit() — a full navigating POST rather than the AJAX add,
         * but the attributes are already written, so the cart is still right.
         */
        try {
          if (form.requestSubmit) form.requestSubmit(submitter);
          else form.submit();
        } catch (err) {
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }
      } finally {
        replaying = false;
      }
    }

    /*
     * A home-delivery add waits for its window and is replayed ONLY when one is
     * in the cart. No timer: the lookup is a Best Buy round trip that can outlast
     * REPLAY_AFTER_MS, and replaying on a timer would add the item with no window
     * — the very order this guard exists to stop. Nothing is stranded either:
     * Hyper's ProductForm never saw this submit, so its button never went busy.
     */
    if (write.hold) {
      bookingForAdd = true;
      write.hold.then(function (go) {
        bookingForAdd = false;
        /*
         * In a new task: the hold can settle through microtasks alone (a window
         * already in the cart, a cached answer), and microtasks run between the
         * listeners of the submit we are holding — a requestSubmit() made then is
         * dropped by the browser as a re-entrant submission. The same defect, and
         * the same fix, as the checkout re-check's replay (2026-10-17).
         */
        if (go) setTimeout(replay, 0);
      }, function () { bookingForAdd = false; });
      return;
    }

    setTimeout(replay, REPLAY_AFTER_MS);
    write.then(replay, replay);
  }, true);

  /* --------------------------------------------------------------- modal */

  var modalOrigin = null; // the element the modal was opened from

  function modalEl() { return document.getElementById('im-pickup-modal'); }

  function openModal(origin) {
    var m = modalEl();
    if (!m) return;
    modalOrigin = origin || null;
    m.hidden = false;
    m.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('im-modal-open');

    var input = m.querySelector('[name="zip"]');
    if (input) {
      input.value = getZip() || '';
      // setTimeout, not rAF — the same document-level click handlers that stole
      // focus from the header search run inside this dispatch. A macrotask runs
      // after the whole dispatch, so this focus survives.
      setTimeout(function () { input.focus(); input.select(); }, 0);
    }
    syncAtcButton();
    if (getZip()) loadStores();
  }

  /* ------------------------------------- closing the drawer with no store chosen
   *
   * Selecting Pickup with no store remembered opens this drawer and writes
   * NOTHING (commitChoice, above). Close it without choosing a store — ×,
   * Escape, the overlay — and until 2026-10-01 the Pickup radio simply stayed
   * selected: the card read "Order now - pickup on … · WEST PALM BEACH FL" (the
   * nearest store to the ZIP, not a chosen one), the cart held only `bb_zip`, and
   * Add to Cart then cleared the fulfilment (choiceWriteForAdd) — so the order
   * shipped while the shopper believed they had chosen pickup. Measured on the
   * live theme, 2026-09-29 and 2026-10-01.
   *
   * WHY THE FIRST FIX WAS INTERMITTENT (2026-09-29, dropped unshipped). It
   * reverted the radio here but bailed out when the cart mirror said
   * `store-pickup` — and the mirror said that whenever the click had fired a
   * `change`, because improvemint-delivery.js's change listener wrote
   * `data-im-cart-fulfillment="store-pickup"` for EVERY pickup selection,
   * including this storeless one that wrote no cart at all. Clicking a Pickup
   * radio that was already checked fires no `change` (the card's click handler
   * opens the drawer instead), so the mirror stayed true and the revert worked.
   * Which of the two happened depended on the previous test's leftovers, which is
   * what looked like a race. That mirror write is gone (improvemint-delivery.js,
   * the `change` listener): the mirror now moves only when a cart write does.
   *
   * So the revert reads the CART, through that mirror: nothing was written, so
   * the selection goes back to the card the cart already stands for — Home
   * Delivery when it holds a complete window, otherwise the shipping card, which
   * is exactly what Liquid renders checked for every other cart — or, when that
   * card is hidden or disabled, the first live card that is not Pickup, which is
   * where ensureSelection() would have put it. It writes nothing, because the
   * cart is already right; only the radio was out of step. With no other live
   * card at all (a pickup-only SKU) Pickup stays: it is the one option there is,
   * and Add to Cart already clears a storeless pickup.
   *
   * WHICHEVER WAY THE DRAWER WAS OPENED. A `change` opens it from commitChoice,
   * but a click on a Pickup radio that is ALREADY checked with no store — which
   * paintPdp does on its own when the shipping card dies — opens it from the
   * card's click handler instead, with no `change` at all. So the test is made
   * here, at the close, from what is on screen: a drawer opened from this
   * product's picker, closing on a storeless Pickup. Not a flag armed on the way
   * in, which the second route would never set.
   *
   * THE ONE REPAINT THAT COULD UNDO IT is paintPdp: an options answer that lands
   * AFTER the close (a ZIP typed into the drawer, a variant switch) and disables
   * the shipping card moves the selection to pickup, and improvemint-delivery.js's
   * ensureSelection() — run inside the same paint — prefers pickup over Home
   * Delivery. paintPdp therefore re-applies this at its end while
   * `pickupDeclined` stands. Nothing else checks the Pickup radio after the
   * drawer closes. A store choice, or any new selection by the shopper, ends it.
   */
  var pickupDeclined = false;

  /* True when Pickup was showing with no store behind it, whether or not
   * anything else could take the selection. */
  function leaveStorelessPickup(root) {
    if (!root) return false;
    var store = getStore();
    if (store && store.id) return false;
    var pickup = root.querySelector('[data-im-fulfillment-option="store-pickup"] input[type="radio"]');
    if (!pickup || !pickup.checked) return false;

    var want = cartHasDeliveryWindow(root) ? 'home-delivery' : 'delivery';
    var radios = root.querySelectorAll('[data-im-fulfillment-option] input[type="radio"]');
    var target = null;
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var card = r.closest('[data-im-fulfillment-option]');
      if (r === pickup || r.disabled || (card && card.hidden)) continue;
      if (r.value === want) { target = r; break; }
      if (!target) target = r;
    }
    // Programmatic, so no `change`: neither file writes, and no Best Buy lookup
    // can be spent on a selection the shopper did not make (F5).
    if (target) target.checked = true;
    return true;
  }

  function closeModal() {
    var m = modalEl();
    if (!m) return;
    m.hidden = true;
    m.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('im-modal-open');
    // commitChoice opens it on the fieldset itself; the card's click handler and
    // the ZIP link open it on the link inside the card. A product card elsewhere
    // on the page opens another product's drawer, and is left alone.
    var root = pdpEl();
    if (root && modalOrigin && root.contains(modalOrigin) && leaveStorelessPickup(root)) {
      pickupDeclined = true;
    }
    if (modalOrigin && modalOrigin.focus) modalOrigin.focus();
    modalOrigin = null;
  }

  function originSku() {
    var pdp = pdpEl();
    var pdpSku = (pdp && pdp.getAttribute('data-im-sku')) || null;
    if (!modalOrigin) return pdpSku;
    // The `no-option` line's ZIP link sits beside the PDP fieldset, not inside
    // it (it lives in the reason line above Add to Cart), and it is about the
    // PDP's own product.
    if (modalOrigin.hasAttribute && modalOrigin.hasAttribute('data-im-none-zip')) return pdpSku;
    var host = modalOrigin.closest('[data-im-sku]');
    return host ? host.getAttribute('data-im-sku') : null;
  }

  function loadStores() {
    var m = modalEl();
    var results = m && m.querySelector('[data-im-store-results]');
    if (!results) return;
    var sku = originSku();
    var zip = getZip();
    if (!sku || !zip) { results.innerHTML = ''; return; }

    results.setAttribute('data-loading', '');
    results.textContent = label('checking', 'Checking stores near {zip}…').replace(/\{zip\}/g, zip);

    fetchOptions(sku, zip).then(function (data) {
      results.removeAttribute('data-loading');
      // The degrade shape (`error`, no `unsellable`) is not a "no" — it read as
      // "This item is not available for in-store pickup" until 2026-10-02. Say
      // what is true: we could not ask.
      if (data && data.error && data.unsellable !== true) {
        results.textContent = label('error', 'Could not reach store availability. Try again shortly.');
        return;
      }
      // PDP: the attribute is on the pickup option; card: on the card itself.
      var restricted = !!(data && data.pickupUnavailableReason === RESTRICTED_REASON) ||
        !!(modalOrigin && modalOrigin.closest('[data-im-pickup-restricted]'));
      var stores = (!restricted && data && data.pickup && data.pickup.stores) || [];
      if (!stores.length) {
        results.textContent = restricted
          ? label('ship-only', 'Available for shipping or delivery only')
          // `pickup: null` for a ZIP with no store (2026-10-28) is that same
          // "none near you", not "never by pickup".
          : data && (data.pickup || data.pickupUnavailableReason === NO_STORES_REASON)
            ? label('no-stores', 'No stores near {zip} have this item right now.').replace(/\{zip\}/g, zip)
            : label('none', 'This item is not available for in-store pickup.');
        return;
      }
      results.innerHTML = '';
      var chosen = getStore();
      var canAdd = !!pdpVariantId() && !pdpPickupRestricted();

      stores.forEach(function (s) {
        /*
         * The whole card is the click target; the small button is a visual
         * affordance inside it. It is a DIV with role="button" rather than a
         * <button>, because a button cannot legally contain another button —
         * browsers drop the inner one, so a nested version would not work.
         * role + tabindex + a keyboard handler restore what the element gives
         * up by not being a real button.
         */
        var card = document.createElement('div');
        card.className = 'im-pickup__store';
        card.setAttribute('data-im-store-pick', '');
        card.setAttribute('data-store-id', s.id);
        card.setAttribute('data-store-name', s.name || '');
        // Store-level, so it may be remembered with the store (chooseStore) —
        // unlike `readyDate`, which is per product and is never stored.
        card.setAttribute('data-store-address', addressLine(s));
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', '0');
        // aria-current, not a data-* attribute: the attribute that draws the
        // chosen tile is the same one that announces it, so they cannot drift
        // apart. 'true' is the token for a non-navigational current item, and
        // the delivery drawer's slots are marked the same way.
        if (chosen && String(chosen.id) === String(s.id)) card.setAttribute('aria-current', 'true');

        var name = document.createElement('span');
        name.className = 'im-pickup__store-name';
        // Best Buy tag mark in front of the store name (GP, 2026-09-14), cloned
        // from the <template> in improvemint-pickup-modal.liquid. Decorative
        // here: every tile would otherwise announce "Best Buy" again, and the
        // dialog title already says whose stores these are.
        var markTpl = m && m.querySelector('template[data-im-bb-mark]');
        var markSvg = markTpl && markTpl.content.firstElementChild;
        if (markSvg) {
          var mark = document.createElement('span');
          mark.className = 'im-pickup__store-mark';
          mark.setAttribute('aria-hidden', 'true');
          var svg = markSvg.cloneNode(true);
          svg.removeAttribute('role');
          svg.removeAttribute('aria-label');
          mark.appendChild(svg);
          name.appendChild(mark);
        }
        name.appendChild(document.createTextNode(s.name || ('#' + s.id)));
        card.appendChild(name);

        // "8 mi. away - 7751 Bird Rd". Both halves are optional: Commerce alone
        // sends neither, and the line is skipped rather than left half-empty.
        var where = [];
        if (s.distance != null && s.distance !== '') {
          where.push(label('distance', '{miles} mi. away').replace(/\{miles\}/g, String(s.distance)));
        }
        if (s.address) where.push(s.address);
        if (where.length) {
          var meta = document.createElement('span');
          meta.className = 'im-pickup__store-meta';
          meta.textContent = where.join(' - ');
          card.appendChild(meta);
        }

        var until = openUntil(s.hours);
        if (until) {
          var hrs = document.createElement('span');
          hrs.className = 'im-pickup__store-meta';
          hrs.textContent = until;
          card.appendChild(hrs);
        }

        // "Ready Tue, Oct 20": this store's final pickup date from the server,
        // the same words the card and the cart line use (2026-10-17).
        var when = pickupWhen(s.readyDate, s.availabilityMsg);
        if (when) {
          var msg = document.createElement('span');
          msg.className = 'im-pickup__store-when';
          msg.textContent = when;
          card.appendChild(msg);
        }

        var act = document.createElement('button');
        act.type = 'button';
        act.className = 'im-pickup__store-atc';
        act.setAttribute('tabindex', '-1'); // the card already takes the tab stop
        // "Add to cart" is the theme's own products.product.add_to_cart, so it
        // reads the same as the product page's button in every language.
        act.textContent = canAdd ? label('add', 'Add to cart') : label('choose-store', 'Choose this store');
        act.setAttribute('aria-hidden', 'true');
        card.appendChild(act);

        // The accessible name carries what the visual rows convey, so a screen
        // reader hears one coherent choice instead of four loose fragments.
        card.setAttribute('aria-label',
          (canAdd ? label('atc-aria', 'Add to cart and pick up at {store}') : label('choose-aria', 'Choose {store}'))
            .replace(/\{store\}/g, s.name || ('#' + s.id)) +
          (where.length ? ', ' + where.join(', ') : '') +
          (until ? ', ' + until : '') +
          (when ? ', ' + when : ''));

        results.appendChild(card);
      });
    }).catch(function () {
      results.removeAttribute('data-loading');
      results.textContent = label('error', 'Could not reach store availability. Try again shortly.');
    });
  }

  /*
   * The drawer's "add to cart" shortcut.
   *
   * The drawer is global and does not know which product opened it, so the
   * variant is read from the product form at click time. It only appears on a
   * page that HAS such a form and only once a store is chosen — adding to cart
   * without a store id would write a cart the backend reads as ship-to-home,
   * which is the opposite of what the button says.
   */
  function pdpVariantId() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    var input = form.querySelector('[name="id"]');
    var v = input && input.value;
    return v ? String(v) : null;
  }

  /*
   * The line-item properties the same product form would post, so this add and
   * the form's land on ONE cart line (see "NO LINE-ITEM PROPERTIES OF ITS OWN" in
   * addToCartWithPickup). Today that is only the visible `Condition`
   * (snippets/improvemint-condition-property.liquid, 2026-10-22), read from the
   * input Liquid rendered into the form, never computed here. Same form as
   * pdpVariantId(), so the variant and its condition cannot come from two
   * products. Hidden `_` keys are deliberately not copied.
   */
  function pdpLineProperties() {
    var form = document.querySelector('form[action*="/cart/add"]');
    var input = form && form.querySelector('[name="properties[Condition]"]');
    return input && input.value ? { Condition: input.value } : null;
  }

  /*
   * The drawer is global: a product card elsewhere on a PDP can open it, and its
   * shortcut adds the PDP's own variant. Never do that for a PDP whose product
   * our lessee can't collect (G4) — the order would fail after payment.
   */
  function pdpPickupRestricted() {
    var root = pdpEl();
    var card = root && root.querySelector('[data-im-fulfillment-option="store-pickup"]');
    return !!(card && card.hasAttribute('data-im-pickup-restricted'));
  }

  /*
   * The drawer's own add-to-cart is a THIRD route into the cart, and it does not
   * go through a form submit — so neither the disabled button nor the capture
   * guard covers it. A store chosen on the previous product is remembered in
   * localStorage, so the shortcut can be offered on a PDP whose SKU nothing can
   * fulfil. Same test both places, for the same reason pdpPickupRestricted() is
   * in both: hidden so it is never reached, and refused if it is. Either kind of
   * block counts (`unsellable` or, since 2026-10-02, `no-option`).
   */
  function pdpSaleBlocked() {
    var root = pdpEl();
    return !!(root && root.hasAttribute('data-im-sale-blocked'));
  }

  function syncAtcButton() {
    var m = modalEl();
    var btn = m && m.querySelector('[data-im-pickup-atc]');
    if (!btn) return;
    var store = getStore();
    btn.hidden = !(pdpVariantId() && store && store.id) || pdpPickupRestricted() || pdpSaleBlocked();
  }

  /*
   * The pending state, and why it is PAINTED rather than substituted.
   *
   * GP, 2026-09-25, QA'ing the live drawer: "instead of showing a button border
   * like artifact that states that is adding to cart, lets make that more a mint
   * green background no border and add a spinner in front of 'Adding to
   * cart...'".
   *
   * The "border like artifact" was this function writing `btn.textContent` when
   * `btn` is the STORE TILE — `chooseStore` passes the card, not the small button
   * inside it, because the card is the click target and the button is
   * `pointer-events: none`. Assigning textContent to that div DESTROYED its four
   * child rows, so a five-line tile collapsed to one line of text inside the
   * tile's own 1px border, every tile below it jumped up, and on failure the
   * restore rebuilt the tile as a single run-together text node
   * ("FRISCO TX2.3 mi. away - …Add to cart") that never recovered.
   *
   * So the tile is left alone and only its BUTTON is swapped — for a mint pill
   * the same height (`--im-store-cta-h`, read by both), carrying a spinner and
   * the words. The tile's height therefore cannot change and nothing below it
   * moves. GP, on the first build, which filled the whole tile with mint: "that
   * card is huge, it should be small height maybe 32px." Zero movement was never
   * in question there; the size was.
   *
   * `disabled` is not available: a div with role="button" ignores it, so the old
   * `btn.disabled = true` only ever set an expando and the tile stayed clickable.
   * The guard is now three things — `aria-disabled` for assistive tech, CSS
   * `pointer-events: none` for the mouse, and the `addingToCart` latch for the
   * keyboard, which `pointer-events` cannot reach (focus stays on the tile, so
   * Enter would otherwise re-fire the add). `aria-busy` says a request is out and
   * the tile's `aria-label` is deliberately left alone, so the accessible name
   * never disappears mid-action.
   */
  // The words come off the drawer (Theme settings → In-store pickup → "Drawer: while adding to cart").
  var addingToCart = false;

  function startAdding(host) {
    addingToCart = true;
    host.setAttribute('data-im-adding', '');
    host.setAttribute('aria-busy', 'true');
    host.setAttribute('aria-disabled', 'true');
    // True only for a real <button>; a div has no such property to set.
    if ('disabled' in host) host.disabled = true;

    var row = document.createElement('span');
    row.className = 'im-pickup__store-adding';
    // Spinner FIRST, per GP: "a spinner in front of". It is a CSS ring — no
    // library, no sprite — and hidden from assistive tech, because the words
    // beside it already say what it means.
    var spin = document.createElement('span');
    spin.className = 'im-pickup__store-spinner';
    spin.setAttribute('aria-hidden', 'true');
    row.appendChild(spin);
    row.appendChild(document.createTextNode(label('adding', 'Adding to cart…')));
    // Straight after the button it stands in for, which CSS then hides — so the
    // pill lands in the button's own slot in the flex column rather than a new
    // one. appendChild would do it too (the button is last), but only by
    // accident of ordering.
    var cta = host.querySelector('.im-pickup__store-atc');
    if (cta && cta.parentNode === host) host.insertBefore(row, cta.nextSibling);
    else host.appendChild(row);
    return row;
  }

  function stopAdding(host, row) {
    addingToCart = false;
    host.removeAttribute('data-im-adding');
    host.removeAttribute('aria-busy');
    host.removeAttribute('aria-disabled');
    if ('disabled' in host) host.disabled = false;
    if (row && row.parentNode) row.parentNode.removeChild(row);
  }

  function addToCartWithPickup(btn) {
    var id = pdpVariantId();
    var props = pdpLineProperties();   // read with the id, before anything async
    var store = getStore();
    if (!id || !store || !store.id || pdpPickupRestricted() || pdpSaleBlocked()) return;
    if (addingToCart) return;

    // The tile is what the shopper pressed and what the state is drawn on; a
    // bare button (the drawer-level shortcut) stands in for itself.
    var host = btn.closest('[data-im-store-pick]') || btn;
    var row = startAdding(host);

    // Attributes FIRST, then the line. If the add succeeds and the attribute
    // write fails, the shopper reaches the cart with an item that will ship —
    // silently the wrong fulfilment. This order fails safe: a failed attribute
    // write stops before anything is added.
    //
    // The pickup date is the PDP product's at this store (pickupFacts), which is
    // the variant this adds — even when a product card elsewhere on the page
    // opened the drawer, because pdpVariantId() is what goes in the cart.
    var facts = pickupFacts(store);
    rememberCart('store-pickup', String(store.id), facts.readyDate);
    updateCart({
      bb_fulfillment: 'store-pickup',
      bb_zip: getZip() || '',
      bb_store_id: String(store.id),
      bb_store_name: store.name || '',
      bb_shipping_option: '',
      bb_delivery_date: '',
      bb_delivery_start: '',
      bb_delivery_end: '',
      bb_delivery_zip: '',
      bb_delivery_earliest: '',
      bb_delivery_services: '',
      bb_pickup_ready_date: facts.readyDate,
      bb_store_address: facts.address
    })
      .then(function () {
        /*
         * NO LINE-ITEM PROPERTIES OF ITS OWN — exactly the ones the PDP form
         * posts, and no others. Since 2026-10-22 that is the visible
         * `Condition` (pdpLineProperties); before that the form posted none, so
         * this posted none. The rule is "the same as the form", for the reason
         * below.
         *
         * GP, 2026-09-25: "same sku shows on two rows in cart/mini-cart instead
         * of qty 2? why is that? sku 6542092". Two rows, one ASUS Vivobook, qty 1
         * each, both labelled "Pickup at Best Buy · NORTH PALM BEACH FL".
         *
         * Shopify's line key is the variant id PLUS a hash of the line's
         * properties, so two adds of one variant merge only when their properties
         * match. This add used to post `_bb_intended: 'store-pickup'` while the
         * PDP form posts none, so the same laptop added once each way became two
         * lines that could never merge — and they looked IDENTICAL, because the
         * "Pickup at Best Buy · <store>" label is rendered from the CART-level
         * attributes, not from the line. Adding twice by the same path always
         * merged, which is why this looked inexplicable.
         *
         * Deleted rather than mirrored onto the form path, because it was
         * write-only. Checked before removing it: `fulfillmentProps` in
         * order-mapping.ts keys its line-property fallback off `_bb_fulfillment`
         * and reads a named list that never included `_bb_intended`; no Liquid
         * reads it (both cart surfaces iterate `item.properties` but skip every
         * `_`-prefixed key — sections/main-cart.liquid:275 and
         * sections/cart-drawer.liquid:206); and nothing else in the theme reads
         * it. Its only observable effect was fragmenting the cart.
         *
         * docs/COMMERCE-PDP-PICKER.md §7 keeps the `_bb_intended` design as the
         * alternative that was NOT built — now with why it must not be revived
         * per-path.
         */
        var line = { id: id, quantity: 1 };
        if (props) line.properties = props;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(line)
        });
      })
      .then(function (r) {
        if (!r || !r.ok) throw new Error('add failed');
        window.location.href = window.Shopify && Shopify.routes && Shopify.routes.root
          ? Shopify.routes.root + 'cart'
          : '/cart';
      })
      .catch(function () {
        // Nothing was destroyed, so there is nothing to rebuild: dropping the
        // pill leaves the tile byte-for-byte as it was rendered.
        stopAdding(host, row);
      });
  }

  function chooseStore(btn) {
    // A cart write is already out. Re-picking now would race it, and on a PDP
    // it would start a second /cart/add for the same tile.
    if (addingToCart) return;
    var card = btn.closest('[data-store-id]') || btn;
    setStore({
      id: card.getAttribute('data-store-id'),
      name: card.getAttribute('data-store-name'),
      // The fallback for bb_store_address when a later answer does not list the
      // store (pickupFacts). Never the date: that is per product.
      address: card.getAttribute('data-store-address') || ''
    });
    pickupDeclined = false; // a store now exists: the Pickup selection is real

    // One click finishes it on a product page: pick the store AND add the item.
    if (pdpVariantId() && !pdpPickupRestricted()) { addToCartWithPickup(btn); return; }

    // If the shopper is already on pickup, re-commit so the cart carries the
    // new store id straight away.
    var picked = document.querySelector('[data-im-fulfillment] input[type="radio"]:checked');
    if (picked && picked.value === 'store-pickup') commitChoice('store-pickup', false);

    repaintCards();
    loadPdp();
    syncAtcButton();

    /*
     * Stay open on a product page. Choosing a store is the step BEFORE the
     * add-to-cart shortcut, so closing here would hide that button the instant
     * it became available — the shopper would never see it. Everywhere else
     * (collection, search) there is nothing further to do, so it closes.
     */
    var m = modalEl();
    var stayOpen = m && m.querySelector('[data-im-pickup-atc]') && !m.querySelector('[data-im-pickup-atc]').hidden;
    if (!stayOpen) closeModal();
  }

  /* ---------------------------------------------------------------- wiring */

  document.addEventListener('click', function (e) {
    var zipTrigger = e.target.closest('[data-im-zip]');
    if (zipTrigger) { e.preventDefault(); openModal(zipTrigger); return; }

    /*
     * The whole Store Pickup card opens the store picker, not just its ZIP link
     * (GP, 2026-09-16). Before this, clicking the card with a store already
     * chosen did nothing at all: the radio was already checked, so no change
     * event fired. And while no ZIP is set the radio is disabled, so the card
     * swallowed the click.
     *
     * Not for a card that can never be picked up — restricted (G4) or
     * unavailable — where the picker would offer something the order refuses.
     *
     * A label click is dispatched twice (label, then its radio), and the change
     * handler may already have opened the modal for a shopper with no store. The
     * macrotask lets that run first; the hidden check makes both idempotent.
     */
    /*
     * Since 2026-10-28 one `unavailable` card does open it: Pickup kept on the
     * page to say no store near this ZIP has the item ([data-im-no-stores]). Its
     * radio stays disabled, and another ZIP is the one thing to do there, which
     * this drawer is for; the line's own "Change ZIP" link opens it too.
     */
    var pickupCard = e.target.closest('[data-im-fulfillment-option="store-pickup"]');
    if (pickupCard && !pickupCard.hasAttribute('data-im-pickup-restricted') &&
        (pickupCard.getAttribute('data-state') !== 'unavailable' || pickupCard.hasAttribute('data-im-no-stores'))) {
      setTimeout(function () {
        var m = modalEl();
        if (m && m.hidden) openModal(pickupCard.querySelector('[data-im-zip]') || pickupCard);
      }, 0);
      return;
    }

    var storeBtn = e.target.closest('[data-im-store-pick]');
    if (storeBtn) { e.preventDefault(); chooseStore(storeBtn); return; }

    var atc = e.target.closest('[data-im-pickup-atc]');
    if (atc) { e.preventDefault(); addToCartWithPickup(atc); return; }

    var closer = e.target.closest('#im-pickup-modal [data-im-close]');
    if (closer) { e.preventDefault(); closeModal(); }
  });

  document.addEventListener('keydown', function (e) {
    var m = modalEl();
    if (e.key === 'Escape' && m && !m.hidden) { closeModal(); return; }

    // The store card is a div with role="button", so it gets no activation for
    // free. Space must also be prevented, or it scrolls the drawer instead.
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      var card = e.target.closest && e.target.closest('[data-im-store-pick]');
      if (card) { e.preventDefault(); chooseStore(card); }
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-im-zip-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('[name="zip"]');
    var err = form.querySelector('[data-im-zip-error]');
    var value = (input && input.value || '').trim();

    if (!/^\d{5}$/.test(value)) {
      if (err) err.hidden = false;
      if (input) input.focus();
      return;
    }
    if (err) err.hidden = true;
    var changed = value !== getZip();
    setZip(value);
    repaintCards();
    loadPdp();
    loadStores();
    // One ZIP, two drawers: tell improvemint-delivery.js, which has to throw
    // away any chosen delivery window (it was offered for the old ZIP, and
    // placement refuses a window read for a different ZIP than the order ships
    // to — DELIVERY_ZIP_MISMATCH).
    if (changed) {
      document.dispatchEvent(new CustomEvent('im:zip-change', { detail: { zip: value, source: 'pickup' } }));
    }
  });

  /*
   * The other drawer changed the ZIP. Repaint from it — but do NOT call
   * loadStores(): that would open a store lookup the shopper did not ask for,
   * on a drawer that is not even open.
   */
  document.addEventListener('im:zip-change', function (e) {
    if (e.detail && e.detail.source === 'pickup') return;   // our own
    repaintCards();
    loadPdp();
  });

  /*
   * improvemint-delivery.js showed or hid the third card. It can do that after
   * this file's paint — on its own init in the warm-cache race, or when its
   * endpoint answers `disabled` / `not-home-delivery` to a shopper's click — and
   * the card it hid may have been the last option on the page. Re-settle from the
   * answer already in hand; no fetch, no cart write. Nothing to settle before the
   * first answer has been painted.
   */
  document.addEventListener('im:home-delivery-card', function () {
    var root = pdpEl();
    if (root && root.__imData !== undefined) settleAvailability(root);
  });

  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var radio = target.closest('[data-im-fulfillment] input[type="radio"]');
    if (radio && radio.checked) {
      // A new selection by the shopper supersedes an earlier dismissal.
      pickupDeclined = false;
      commitChoice(radio.value, true);
      return;
    }

    // The variant picker moved. See syncPdpSku for why this event and not
    // `variant:changed`.
    if (target.name === 'id' && target.closest('form[action*="/cart/add"]')) syncPdpSku();
  });

  /*
   * Coming soon: product-info.js's toggleAddButton and the sticky bar write
   * Hyper's "Sold out" into the add button after every variant change, then
   * dispatch `variant:changed`. Only the timing is used here, never its
   * `detail.variant` (see syncPdpSku for why that would be the wrong variant).
   */
  document.addEventListener('variant:changed', function () {
    var root = pdpEl();
    if (root && releaseState(root) === 'coming-soon') applySaleBlock(root, 'coming-soon');
  });

  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest('[data-im-release-reload]');
    if (!t) return;
    e.preventDefault();
    window.location.reload();
  });

  function init() {
    repaintCards();
    refineUnknownCards();
    loadPdp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // AJAX collection grids and quick-views swap cards in without a reload.
  document.addEventListener('shopify:section:load', init);

  /*
   * Cards that arrive AFTER load. "Recently viewed products" on the cart page
   * fetches its cards once the page is up, so init() had already run and those
   * cards kept their Liquid default — "Pickup in store · Enter ZIP" — even with
   * a ZIP saved (GP, 2026-09-16). The same was true of anything else injected
   * later: AJAX filters, predictive search, recommendations.
   *
   * Observes childList only. Painting a card rewrites text inside it, which is
   * also a childList mutation — but the nodes it adds are Text, never an element
   * carrying [data-im-pickup], so the filter below ignores our own writes and
   * this cannot loop. Both repaint and refine are idempotent (refine marks
   * data-im-refined), so a burst of insertions costs one debounced pass.
   */
  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function (records) {
      var found = false;
      for (var i = 0; i < records.length && !found; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.matches('[data-im-pickup]') || n.querySelector('[data-im-pickup]'))) {
            found = true;
            break;
          }
        }
      }
      if (!found || pending) return;
      pending = setTimeout(function () {
        pending = null;
        repaintCards();
        refineUnknownCards();
      }, 50);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
